/**
 * Murphy Door Configurator - Core Constants
 *
 * PURPOSE: Centralized constants for material types, event names, and shared values
 * PHASE: 0 (Pre-bootstrap - must load BEFORE all other modules)
 * CCP STATUS: FOUNDATION MODULE - Provides constants to all other modules
 *
 * RESPONSIBILITIES:
 * 1. Define material type constants (wood, finish)
 * 2. Define all event name constants for pub/sub system
 * 3. Provide frozen, immutable constant objects
 * 4. Eliminate magic strings across codebase
 *
 * ARCHITECTURE:
 * - All constants are Object.freeze() for immutability
 * - Nested objects are also frozen to prevent tampering
 * - Loaded FIRST before event-bus or any other module
 *
 * USAGE:
 *   const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;
 *   coordinator.applyMaterialPreference(CONSTANTS.MATERIAL_TYPES.WOOD, gid, context);
 *   eventBus.publish(CONSTANTS.EVENTS.MATERIAL_PREFERENCE_APPLIED, payload);
 *
 * EVENT COUNT: 582 events across 20 namespaces
 */

(function () {
  'use strict';

  // Dev-only logging helper

  // Initialize namespace
  if (!window.MurphyDoorConfigurator) {
    window.MurphyDoorConfigurator = {};
  }

  // Define foundational constants as local variables first so LAYER_SUPPRESSION_DEPS
  // can reference them without hardcoded strings (DRY — single source of truth).
  const _CATEGORY_NAMES = Object.freeze({
    DELIVERY: 'Delivery',
    TOP_KNOBS_PULLS: 'Top Knobs & Pulls',
    TOP_CABINET_DOORS: 'Top Cabinet Doors',
    BOTTOM_KNOBS_PULLS: 'Bottom Knobs & Pulls',
    BOTTOM_CABINET_DOORS: 'Bottom Cabinet Doors',
    DOOR_HANDING: 'Door Handing',
  });

  const _PRODUCT_HANDLES = Object.freeze({
    RUSH_FEE_UPGRADE: 'rush-fee-upgrade',
    NO_TOP_CABINET_DOORS: 'no-top-cabinet-doors-single-french',
    NO_BOTTOM_CABINET_DOORS: 'no-lower-cabinet-doors-single-french',
    // Appearance Upgrades' $750 "Add LEDs to interior of door" — no 3D asset; the 3D
    // renderer draws its glow (md-config-3d-renderer.js LED_RIG).
    LED_INTERIOR_UPGRADE: 'add-leds-to-interior-of-door',
  });

  // ---------------------------------------------------------------------------
  // Admin-tunable inset-nudge amounts (percent of frame height).
  // The md-configurator.liquid section injects window.MurphyDoorConfigurator
  // .insetNudgeConfig { upperPercent, lowerPercent } via an inline script BEFORE
  // this module executes (classic inline scripts run before deferred modules).
  // Negative = nudge UP, positive = nudge DOWN. Any finite decimal is accepted;
  // values are clamped to ±100% (beyond a full frame height is never intended)
  // and fall back to the defaults below when unset/invalid (e.g. the prewarm
  // template, which has no section settings). Prod defaults: upper -0.5% (up) and
  // lower +0.5% (down) — an inset cabinet reads as smaller, so its knobs shift
  // toward the door center (upper knobs up, lower knobs down).
  const _INSET_UPPER_NUDGE_DEFAULT = -0.5;
  const _INSET_LOWER_NUDGE_DEFAULT = 0.5;
  // Namespace is guaranteed to exist here (initialized at the top of this IIFE).
  const _insetNudgeConfig =
    window.MurphyDoorConfigurator.insetNudgeConfig || {};
  const _resolveNudgePercent = (rawValue, fallback) => {
    // Nil / empty / whitespace-only (setting cleared or blanked in the theme
    // editor) → default. Do NOT Number() these: Number(null), Number(''), and
    // Number('   ') all coerce to 0, which would silently override the default
    // with a no-nudge instead of falling back.
    if (rawValue === null || rawValue === undefined) return fallback;
    const trimmed = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    if (trimmed === '') return fallback;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) return fallback; // NaN / Infinity → default
    return Math.max(-100, Math.min(100, parsed)); // clamp to a full frame height
  };
  const _INSET_UPPER_NUDGE = _resolveNudgePercent(
    _insetNudgeConfig.upperPercent,
    _INSET_UPPER_NUDGE_DEFAULT
  );
  const _INSET_LOWER_NUDGE = _resolveNudgePercent(
    _insetNudgeConfig.lowerPercent,
    _INSET_LOWER_NUDGE_DEFAULT
  );

  // Define and freeze all constants
  window.MurphyDoorConfigurator.CONSTANTS = Object.freeze({
    // =============================================================================
    // MATERIAL TYPES
    // =============================================================================
    MATERIAL_TYPES: Object.freeze({
      WOOD: 'wood',
      FINISH: 'finish',
    }),

    // =============================================================================
    // CATEGORY NAMES - Canonical category names from Shopify admin
    // =============================================================================
    // Centralizing these prevents silent failures if names change in Shopify admin.
    // All modules that check category identity MUST use these constants.
    CATEGORY_NAMES: _CATEGORY_NAMES,

    // =============================================================================
    // PRODUCT HANDLES - Canonical Shopify product handles
    // =============================================================================
    // Centralizing these prevents silent failures if handles change in Shopify admin.
    // All modules that check product identity MUST use these constants.
    PRODUCT_HANDLES: _PRODUCT_HANDLES,

    // =============================================================================
    // CART VISIBLE PROPERTY NAMES - Non-underscore properties visible to customers
    // =============================================================================
    // These appear in the cart drawer/page as line item properties.
    // Underscore-prefixed properties (_Price, _Category, etc.) are hidden by Liquid
    // template filter `property_first_char != '_'` — these are the customer-facing ones.
    CART_VISIBLE_PROPERTY_NAMES: Object.freeze({
      DOOR: 'Door',
    }),

    // =============================================================================
    // LAYER SUPPRESSION DEPENDENCIES - Knob layers suppressed when "no doors" applied
    // =============================================================================
    // Maps a dependent category (knobs) to its parent category (doors).
    // When the parent has a "no doors" product applied, the dependent's image layer
    // is visually suppressed (the selection itself is preserved for cart purposes).
    // Keys are CATEGORY_NAMES values (used as appliedSelections Map keys).
    LAYER_SUPPRESSION_DEPS: Object.freeze({
      [_CATEGORY_NAMES.TOP_KNOBS_PULLS]: Object.freeze({
        dependsOnCategory: _CATEGORY_NAMES.TOP_CABINET_DOORS,
        suppressWhenProductHandle: _PRODUCT_HANDLES.NO_TOP_CABINET_DOORS,
      }),
      [_CATEGORY_NAMES.BOTTOM_KNOBS_PULLS]: Object.freeze({
        dependsOnCategory: _CATEGORY_NAMES.BOTTOM_CABINET_DOORS,
        suppressWhenProductHandle: _PRODUCT_HANDLES.NO_BOTTOM_CABINET_DOORS,
      }),
    }),

    // =============================================================================
    // INSET NUDGE DEPENDENCIES - Knob/pull overlay nudged when paired door is inset
    // =============================================================================
    // Super-app bakes the base placement of every knob/pull overlay. The theme applies
    // one conditional delta at display time: when the paired cabinet DOOR is inset
    // (the merchant-owned custom.is_inset boolean, stamped through the PPD and read by
    // computeInsetNudge/detectInsetFromResolved), the knob/pull layer shifts by nudgePercent (percent of frame
    // height via CSS translateY, so it's resolution-independent). Negative = up, positive =
    // down. Both upper and lower are wired; the amounts are admin-tunable per the "Inset
    // Door Knob/Pull Nudge" section settings (see the _INSET_*_NUDGE resolution above). A
    // nudgePercent of 0 is a no-op (equivalent to absence).
    // Keys are CATEGORY_NAMES values (used as appliedSelections Map keys).
    INSET_NUDGE_DEPS: Object.freeze({
      [_CATEGORY_NAMES.TOP_KNOBS_PULLS]: Object.freeze({
        dependsOnCategory: _CATEGORY_NAMES.TOP_CABINET_DOORS,
        nudgePercent: _INSET_UPPER_NUDGE,
      }),
      [_CATEGORY_NAMES.BOTTOM_KNOBS_PULLS]: Object.freeze({
        dependsOnCategory: _CATEGORY_NAMES.BOTTOM_CABINET_DOORS,
        nudgePercent: _INSET_LOWER_NUDGE,
      }),
    }),

    // =============================================================================
    // CATEGORY VISIBILITY DEPENDENCIES - synthetic "gate card" driven DOM show/hide
    // =============================================================================
    // Keys are gate IDs (synthetic, NOT real CATEGORY_NAMES entries — a gate answer
    // is UI/session state, never a real appliedSelections entry). Each gate lists the
    // real categories it controls and, per answer value, which of those categories
    // should be visible. Categories omitted from an answer's visible list are hidden
    // AND, if listed in autoApplyOnHide, get the named "no doors" product handle
    // auto-applied through the real one-step apply pipeline — required because
    // allRequiredCategoryIdsArray has no "optional category" concept (see
    // md-config-core-data-pipeline.js classifyCategory/tabStructure build): a hidden
    // category with no applied selection would permanently block Add to Cart.
    CATEGORY_VISIBILITY_DEPS: Object.freeze({
      cabinets: Object.freeze({
        categories: Object.freeze([
          _CATEGORY_NAMES.TOP_CABINET_DOORS,
          _CATEGORY_NAMES.BOTTOM_CABINET_DOORS,
        ]),
        answers: Object.freeze({
          none: Object.freeze([]),
          top_only: Object.freeze([_CATEGORY_NAMES.TOP_CABINET_DOORS]),
          bottom_only: Object.freeze([_CATEGORY_NAMES.BOTTOM_CABINET_DOORS]),
          top_and_bottom: Object.freeze([
            _CATEGORY_NAMES.TOP_CABINET_DOORS,
            _CATEGORY_NAMES.BOTTOM_CABINET_DOORS,
          ]),
        }),
        autoApplyOnHide: Object.freeze({
          [_CATEGORY_NAMES.TOP_CABINET_DOORS]:
            _PRODUCT_HANDLES.NO_TOP_CABINET_DOORS,
          [_CATEGORY_NAMES.BOTTOM_CABINET_DOORS]:
            _PRODUCT_HANDLES.NO_BOTTOM_CABINET_DOORS,
        }),
      }),
    }),

    // =============================================================================
    // GATE COMBINATION RESOLUTIONS - two gate answers -> one variant on ONE
    // real category (architecturally distinct from CATEGORY_VISIBILITY_DEPS,
    // which maps one gate's answer to a set of DIFFERENT categories to show)
    // =============================================================================
    // EMPTY ON PURPOSE (2026-08-27, Ryan): the machinery below stays, the one
    // entry it ever held is gone.
    //
    // "Door Handing" is a single composite product (door-handling-single-door)
    // with exactly 4 variants -- "Left Hand Inswing", "Right Hand Inswing",
    // "Left Hand Outswing", "Right Hand Outswing" -- each carrying its own real
    // configurator thumbnail. It used to be hidden behind a two-step Swing
    // Direction? -> Hinge Side? gate pair that recombined the two answers back
    // into exactly those 4 variants. That is two questions and a dependency
    // edge to reach a 4-tile grid the category already renders natively, so the
    // gates were removed and Door Handing now shows directly as ONE card.
    //
    // Collapsing it also retires a whole class of dead end this file documented
    // at length: because the gates kept Door Handing permanently display:none,
    // a forbidden swing/hinge combination refused its apply against an
    // invisible card and auto-navigation stranded the customer (live-reproduced
    // 2026-08-13 and again 2026-08-24). A real, visible card refuses a
    // forbidden tile the same way every other category does, in front of the
    // customer.
    //
    // Kept as an empty frozen array rather than deleted: every consumer already
    // reads it as `CONSTANTS.GATE_COMBINATION_RESOLUTIONS || []`, and the two
    // pieces that matter -- resolving answers into one applied variant
    // (md-config-bl-gate-card-coordinator.js) and hiding the resolved category
    // (md-config-ui-gate-visibility-manager.js) -- are still here and still
    // written generically off gateIds/variantTitles.
    //
    // What a future combination gate would NOT get back for free: per-option
    // product imagery on the gate tiles. applyGateOptionImages() was swept with
    // the gates on 2026-08-27 -- it degrades to "gate options keep whatever
    // image their definition gives them", not to a broken gate. The forbidden
    // pre-computation that went with it should NOT come back at all; see the
    // header of md-config-ui-gate-visibility-manager.js for why.
    GATE_COMBINATION_RESOLUTIONS: Object.freeze([]),

    // =============================================================================
    // SELECTION VISIBILITY DEPENDENCIES - reveal driven by a REAL applied
    // selection, not a gate answer (second progressive-disclosure layer)
    // =============================================================================
    // Keyed by the dependent real category (e.g. Top Knobs & Pulls). Hidden
    // until the parent category (Top Cabinet Doors) has a real applied
    // selection whose product handle is NOT the "no doors" handle -- i.e.
    // an actual cabinet-door style was chosen, not just the gate's
    // auto-applied "no doors" fallback. Structurally similar to
    // LAYER_SUPPRESSION_DEPS (same dependsOnCategory/handle shape) but a
    // DIFFERENT consumer (DOM card visibility here, image-layer suppression
    // there) -- kept as separate constants per the same reasoning
    // CATEGORY_VISIBILITY_DEPS was kept separate from LAYER_SUPPRESSION_DEPS.
    //
    // autoApplyVariantTitleOnHide: same permanently-hidden-blocks-ATC problem
    // CATEGORY_VISIBILITY_DEPS.autoApplyOnHide solves for Cabinet Doors,
    // but Top/Bottom Knobs & Pulls have no standalone "no knobs" PRODUCT --
    // it's a VARIANT ("No Knobs or Pulls") inside the same composite
    // product returned by getVirtualizedProductsForCategory(categoryName),
    // so resolution is by variant title, not product handle. Consumed by
    // md-config-bl-gate-card-coordinator.js in the SAME bulk-apply batch as
    // the parent Cabinet Doors "no doors" apply (confirmed live 2026-08-11:
    // selecting Cabinets? -> None hid the knobs categories but left them
    // permanently unapplied, locking Add to Cart / progress at 60%).
    SELECTION_VISIBILITY_DEPS: Object.freeze({
      [_CATEGORY_NAMES.TOP_KNOBS_PULLS]: Object.freeze({
        dependsOnCategory: _CATEGORY_NAMES.TOP_CABINET_DOORS,
        hiddenWhenProductHandle: _PRODUCT_HANDLES.NO_TOP_CABINET_DOORS,
        autoApplyVariantTitleOnHide: 'No Knobs or Pulls',
      }),
      [_CATEGORY_NAMES.BOTTOM_KNOBS_PULLS]: Object.freeze({
        dependsOnCategory: _CATEGORY_NAMES.BOTTOM_CABINET_DOORS,
        hiddenWhenProductHandle: _PRODUCT_HANDLES.NO_BOTTOM_CABINET_DOORS,
        autoApplyVariantTitleOnHide: 'No Knobs or Pulls',
      }),
    }),

    // =============================================================================
    // CASCADE RESTORE CONTEXTS - which cascade `reason`/`context` values mean
    // "the customer's own saved picks are being put back"
    // =============================================================================
    // A `?config=` link (and the editor bridge, which the cascade coordinator
    // also labels 'url_restoration') or a "Build this" preset. Both carry the
    // customer's picks, so a gate answer the customer gave EARLIER in the tab
    // may be re-derived from the doors a restore actually wrote (Epic B2,
    // 2026-10-09: a warm "Cabinets? None" hid restored, still-charged doors).
    // Deliberately NOT 'willitfit_prefill' or 'simplified_mode': those are
    // suggestions, never a customer's own build, and must never move a gate
    // answer the customer gave. The cascade coordinator keeps its own local
    // BUILD_PRESET_CONTEXTS for provenance stamping; this list is the restore
    // meaning only. Consumer: md-config-bl-gate-card-coordinator.js.
    CASCADE_RESTORE_CONTEXTS: Object.freeze([
      'url_restoration',
      'build_preset',
    ]),

    // =============================================================================
    // DERIVED PRODUCT RULES - a category whose PRODUCT follows another
    // category's applied selection (the customer only picks the variant)
    // =============================================================================
    // Extra Shelves (FMD2, 2026-09-30) mirrors real MageWorx APO's two options,
    // "Extra Standard Adjustable Shelves" and "Extra Lipped Adjustable Shelves"
    // (1-10 shelves, ES1-ES10 / EL1-EL10). The catalog holds one product per
    // shelf style (display_name "Standard Shelves" / "Lipped Shelves"), each
    // with the same count variants ("0 Extra Shelves" ... "10 Extra Shelves").
    // The style is already answered on Shelf Style, so the category shows only
    // the product whose display name is contained in Shelf Style's applied
    // product label. Exactly one match is required; otherwise the category
    // falls back to the product that owns its configured default.
    //
    // Consumers, all through the conductor (never this map directly):
    //   conductor.getSliderMetadata()        slider stops = matched product only
    //   md-config-bl-derived-variant-coordinator.js   style changes keep the count
    //   md-config-ui-horizontal-slider.js    re-renders when the source changes
    DERIVED_PRODUCT_RULES: Object.freeze({
      'Extra Shelves': Object.freeze({ sourceCategory: 'Shelf Style' }),
    }),

    // =============================================================================
    // GATE CARD DEFINITIONS - synthetic question cards injected by buildCategories()
    // =============================================================================
    // Drives the gate card's own markup/options. See CATEGORY_VISIBILITY_DEPS (above)
    // for what each answer reveals/hides. Gated entirely behind
    // conductor._areGateCardsEnabled() — a no-op array when that returns false.
    GATE_CARD_DEFINITIONS: Object.freeze([
      Object.freeze({
        id: 'cabinets',
        insertBeforeCategory: _CATEGORY_NAMES.TOP_CABINET_DOORS,
        labelHTML: 'Cabinets?',
        options: Object.freeze([
          Object.freeze({ value: 'none', label: 'None' }),
          Object.freeze({ value: 'top_only', label: 'Top Only' }),
          Object.freeze({ value: 'bottom_only', label: 'Bottom Only' }),
          Object.freeze({ value: 'top_and_bottom', label: 'Top and Bottom' }),
        ]),
      }),
    ]),

    // =============================================================================
    // APO STYLE CATEGORY ORDER - two render sequences for the apo_style experience
    // =============================================================================
    // Consumed exclusively by _revealAllCategoriesForApoStyle() in
    // md-config-ui-navigation-manager.js to re-sort the tab-concatenated reveal list.
    // classic/dual_* never read either constant — they keep the admin-driven
    // loadOrder/displayOrder sequence unchanged.
    // Real category names are raw string literals (admin-driven data, not a code enum —
    // see _CATEGORY_NAMES above, which is deliberately scoped to cross-category
    // dependency wiring only). Gate cards use 'gate:<id>' matching GATE_CARD_DEFINITIONS.
    // Any category rendered live but absent from a list falls back to the end of the
    // sequence in its existing relative order -- never silently dropped.
    //
    // Category naming is NOT fully consistent product-to-product (confirmed live
    // 2026-08-12 on Flush Mount Bookcase Door 2.0): the same conceptual step shows up
    // under different real names on different products (e.g. 'Door Depth' here vs.
    // 'Door Pocket Depth' on the "Door Bookcase" fixture product; 'Top Section'/
    // 'Bottom Section' here vs. 'Top Pocket'/'Bottom Pocket' there). Where a known
    // synonym exists, both names are listed at the same sequence position so either
    // product renders correctly -- add further synonyms here as they're discovered
    // live, rather than assuming one canonical name covers the whole catalog.

    // "1:1 APO" mode ON (conductor._isApo1to1Enabled() === true) — the literal
    // MageWorx step order, appearance and specs interleaved exactly as APO does it.
    //
    // 'Top Pocket'/'Top Section' (+ Bottom) sit BEFORE 'gate:cabinets' -- confirmed
    // with Daniel 2026-08-12: these are the real shelf-type category (live data:
    // "Standard Shelves" vs "Lipped Shelves" -- the "Shelf type" step from Daniel's
    // original 17-step spec, never confirmed live before today under the placeholder
    // name 'Shelf Quantity', which is now retired). Shelves are required regardless
    // of the Cabinets answer and the Cabinets answer affects whether the shelf is
    // visible, so the shelf question must be answered first. This also fixes a real
    // bug: with shelf cards positioned AFTER the gate, answering "Cabinets?" made
    // auto-nav jump straight to the (still-unanswered) shelf card as if it had been
    // skipped, which read as broken/confusing.
    //
    // Top/Bottom (Pocket/Section) are grouped as adjacent pairs (2026-08-13,
    // Daniel): they're each still their own real, independently visible/required
    // category -- NOT one hidden behind the other. Real MageWorx APO applies one
    // shelf-type pick to both top and bottom, so md-config-bl-shelf-mirror-coordinator.js
    // keeps them in sync (either card drives the other), but both cards stay in the
    // DOM/order/auto-nav walk like any other category. An earlier version of this
    // (2026-08-13, same day) hid the Bottom card and relied entirely on the mirror
    // to satisfy completion -- reverted after it broke Add to Cart in a way that
    // wasn't reproducible in isolation; keeping both cards real/visible is the
    // simpler, lower-risk shape.
    //
    // 'Shelf Style' (2026-09-16) is the merged single-step replacement for
    // Top/Bottom Pocket/Section on flush-mount-bookcase-door-2-0's Single Door
    // line -- added at the SAME sequence position, alongside the four old
    // names rather than replacing them. This constant is global, not
    // per-product, and Archway Bookcase Door 2.0 still uses the real
    // 'Top Section'/'Bottom Section' categories (shared metaobjects) with the
    // granular two-step flow -- removing them here would silently reorder
    // Archway's steps even though nothing else about this change touches it.
    // An entry with no matching live category on a given product is simply
    // never looked up, so keeping all five costs nothing.
    APO_STYLE_1TO1_CATEGORY_ORDER: Object.freeze([
      'Wood & Finish',
      'Width',
      'Height',
      'Jamb Depth',
      _CATEGORY_NAMES.DOOR_HANDING,
      // Ryan, UAT 2026-08-27: Door Depth moves AFTER Door Handing in 1:1 mode.
      //
      // Both synonym spellings move together and must stay adjacent -- they are
      // ONE step under two names ('Door Depth' here, 'Door Pocket Depth' on the
      // "Door Bookcase" fixture product), so splitting them would put the same
      // question in two different positions depending on which product loaded.
      //
      // Safe in both directions despite the depth<->swing forbidden rules
      // ("Depth 5-1/4\" only allows Out-Swing", "Inswing 6.5\" Jamb
      // Restriction"). Verified live 2026-08-27 on the now-visible Door Handing
      // card: with a handing applied first, the conflicting depth tiles grey out
      // with the real rule text, exactly as the handing tiles grey out when a
      // depth is applied first. Neither order strands the customer.
      'Door Pocket Depth',
      'Door Depth',
      'Top Pocket',
      'Bottom Pocket',
      'Top Section',
      'Bottom Section',
      'Shelf Style',
      'Extra Shelves',
      'gate:cabinets',
      'gate:side',
      _CATEGORY_NAMES.TOP_CABINET_DOORS,
      _CATEGORY_NAMES.TOP_KNOBS_PULLS,
      _CATEGORY_NAMES.BOTTOM_CABINET_DOORS,
      _CATEGORY_NAMES.BOTTOM_KNOBS_PULLS,
      // Front-facing categories clustered together, then back-facing ones
      // (Daniel 2026-08-13): each category's product images auto-toggle the
      // gallery's front/back camera view to match (media-gallery.js's
      // handleAutoViewToggle) -- interleaving Front/Back/Front/Back forced a
      // camera flip on every single step. 'Door Backing' groups with the
      // back cluster (its name and live tab-discovery position both point to
      // back-side relevance, even though it's not literally "Back "-prefixed).
      'Front Casing',
      'Front Entablature',
      'Back Casing',
      'Back Entablature',
      'Door Backing',
      'Locking Systems',
      'Smart Home Automations',
      'Appearance Upgrades',
      _CATEGORY_NAMES.DELIVERY,
    ]),

    // "1:1 APO" mode OFF (default apo_style presentation) — every visible
    // customization option clustered first (confirmed with Daniel 2026-08-12:
    // "all the visible customization options are first... wood and finish, cabinets,
    // shelves, casings should all be next to each other"), then measurements, then
    // Locking Systems (which depends on the measurement answers, so must render
    // after them), then the lower-priority upsell categories, then Delivery.
    // See the 1:1 order's comment above for why 'Top/Bottom Pocket'/'Top/Bottom
    // Section' sit before 'gate:cabinets' rather than after it, and for why
    // 'Shelf Style' (2026-09-16) is added alongside them rather than replacing
    // them -- this constant is global and Archway Bookcase Door 2.0 still uses
    // the real Top/Bottom Section categories.
    APO_STYLE_DEFAULT_CATEGORY_ORDER: Object.freeze([
      'Wood & Finish',
      _CATEGORY_NAMES.DOOR_HANDING,
      'Top Pocket',
      'Bottom Pocket',
      'Top Section',
      'Bottom Section',
      'Shelf Style',
      'Extra Shelves',
      'gate:cabinets',
      'gate:side',
      _CATEGORY_NAMES.TOP_CABINET_DOORS,
      _CATEGORY_NAMES.TOP_KNOBS_PULLS,
      _CATEGORY_NAMES.BOTTOM_CABINET_DOORS,
      _CATEGORY_NAMES.BOTTOM_KNOBS_PULLS,
      // Front-facing categories clustered together, then back-facing ones
      // (Daniel 2026-08-13): each category's product images auto-toggle the
      // gallery's front/back camera view to match (media-gallery.js's
      // handleAutoViewToggle) -- interleaving Front/Back/Front/Back forced a
      // camera flip on every single step. 'Door Backing' groups with the
      // back cluster (its name and live tab-discovery position both point to
      // back-side relevance, even though it's not literally "Back "-prefixed).
      'Front Casing',
      'Front Entablature',
      'Back Casing',
      'Back Entablature',
      'Door Backing',
      'Width',
      'Height',
      'Door Pocket Depth',
      'Door Depth',
      'Jamb Depth',
      'Locking Systems',
      'Smart Home Automations',
      'Appearance Upgrades',
      _CATEGORY_NAMES.DELIVERY,
    ]),

    // =============================================================================
    // MATERIAL SOURCE CATEGORIES - the card(s) that CHOOSE the wood/finish
    // =============================================================================
    // Consumed by md-config-bl-material-remap-coordinator.js, which must skip
    // these when carrying selections across a material change.
    //
    // Why they need excluding: a wood card's products ARE the woods -- "Paint
    // Grade (Single Door)" only ever has PG variants, "White Oak Veneer
    // (Single Door)" only WO. So on any swap the previously-applied wood
    // product has no variant in the new material, and a naive remap flags the
    // material card itself as "unavailable in the new stain" and raises the
    // reset warning on EVERY swap -- including ones where every real component
    // carries across fine. Confirmed live 2026-08-17: a PG/WH -> WO/UN swap
    // planned a correct cabinet remap and still reported Wood & Finish
    // unmappable.
    //
    // Changing the material here is the customer's intent, not a casualty of
    // it, so this category is never something to warn about.
    MATERIAL_SOURCE_CATEGORIES: Object.freeze(['Wood & Finish']),

    // =============================================================================
    // "BUILD THIS" CUSTOMER PRESETS (md-config-support-build-preset.js)
    // =============================================================================
    // A preset applies a real customer's door LOOK and never a measurement or a
    // site-specific option. These categories are deleted from the decoded preset
    // BEFORE validation, even when the metaobject carries them. Measurements are
    // sacred: undo never touches them either, because they are never in the
    // preset's category set.
    BUILD_PRESET_STRIP_CATEGORIES: Object.freeze([
      'Width',
      'Height',
      'Door Depth',
      'Jamb Depth',
      'Door Handing',
      'Delivery',
      // 2026-10-01 (owner decision): Extra Shelves is the customer's own
      // QUANTITY, not part of the door's look, so it is treated like a
      // measurement. Being a DERIVED_PRODUCT_RULES category, the support
      // module also keeps its COUNT across the preset's Shelf Style change
      // (same count on the product the new style implies).
      'Extra Shelves',
    ]),
    // Metaobject handle, stable forever, doubles as the _Build_Preset_ID cart
    // property. Validated BEFORE it is used in any selector or state write.
    BUILD_PRESET_HANDLE_REGEX: /^bp-[0-9a-f]{12}$/,
    // ?build_preset=<handle> share link. Never persisted: the parser and the
    // persistence manager delete it on every URL write.
    BUILD_PRESET_URL_PARAM: 'build_preset',
    // BUSINESS_IMAGE_LAYER_OPERATION_* `operation` of the image-layer
    // coordinator's settled repaint after a preset apply (context
    // 'build_preset'). The UI module hides the progress overlay on it.
    BUILD_PRESET_LAYER_SYNC_OPERATION: 'build_preset_layer_sync',
    // ?md_render_job=1 unlocks the read-only render hook
    // (imageExportManager.renderConfigurationImage). Latched at parser load.
    RENDER_JOB_URL_PARAM: 'md_render_job',

    // =============================================================================
    // PPD CACHE CONTRACT VERSION
    // =============================================================================
    // Bump this by hand whenever a change alters the CONTENT of anything the
    // pipeline bakes into the cached perfectProductDictionary -- most of all the
    // Layer 5 pre-rendered HTML maps.
    //
    // 🚨 WHY THIS CANNOT BE AUTOMATIC. There is no build step in this theme, so
    // nothing can derive a code version. `checkPPDCache()` validates
    // productVersion/globalVersion (webhook-driven, and they move ONLY when
    // Shopify CONTENT changes), a catalog hash of add-on ids, and a 7-day TTL.
    // A theme JS deploy moves none of them. So a visitor holding a PPD cached
    // before the deploy is served the pre-deploy shape, unvalidated, for up to
    // seven days.
    //
    // This is not hypothetical, and "please refresh" does not rescue it: a
    // refresh revalidates the SAME entry against the SAME unchanged version and
    // hash, and returns the same cache.
    //
    // Evidenced twice:
    //   - Whole-dollar price stripping (see the CLAUDE.md entry): tile HTML
    //     cached before the deploy still read "$600.00", which is exactly why
    //     the rush-caption pattern carries a `(?:\.00)?` tolerance -- a
    //     workaround for stale cache that this constant is the real fix for.
    //   - 66694dfe3 (2026-02-04): standalone product ids were not normalized to
    //     strings, so Layer 5 map .set()/.get() mismatched. Live 5 days.
    //
    // The sibling defence is ppdExpressesCurrentContract() in
    // md-config-core-initializer.js, which catches SHAPE regressions without
    // anyone remembering anything. It cannot catch content changes -- a stale
    // "$600.00" tile resolves perfectly well. That is this constant's job, and
    // forgetting to bump it is its failure mode.
    // BUMP LOG (append a line every time this moves -- the number alone tells a
    // future reader nothing about which stale shape it was defending against):
    //   1 -> 2 (2026-09-04): configurator thumbnails gained `srcset`/`sizes`.
    //     Layer 5 tile HTML cached before this deploy carries a bare `<img src>`
    //     with a build-time, DPR-STAMPED width, so a returning 3x-DPR visitor who
    //     first cached on a 1x device keeps a 100px image forever. Exactly the
    //     content-not-shape case above: ppdExpressesCurrentContract() cannot see
    //     it, because a srcset-less tile is structurally valid.
    //   2 -> 3 (2026-09-11): Custom Jamb Depth's productToVariantLayoutHTMLMap
    //     entry changed from a 133-tile variant grid to a numeric input card
    //     (NUMERIC_INPUT_PRODUCTS below). Same map, same key, pure content-shape
    //     change the 2026-09-04 incident this constant exists for describes
    //     exactly — the 2026-09-09 content-fingerprint mechanism hashes raw
    //     variant ids/prices/availability and cannot see a rendering-HTML-choice
    //     change, so this version bump is the ONLY thing that invalidates a
    //     cached customer's stale 133-tile cache.
    //   3 -> 4 (2026-09-15): buildImageLayerStructure (md-config-core-data-
    //     pipeline.js) now prefers a category's configured default-variant
    //     product over whichever product happened to iterate first when
    //     picking imageLayerStructure's per-category bootstrap image. Same map,
    //     same key, same raw variant ids/prices/availability — only WHICH
    //     product's image gets baked in changed (Appearance Upgrades' $750 LED
    //     upgrade product was winning over "No Appearance Upgrades", so the LED
    //     glow overlay rendered as if applied on every fresh load). The exact
    //     content-not-shape case this constant exists for: a visitor with a
    //     pre-fix PPD cached in IndexedDB kept the LED image forever, and no
    //     number of hard refreshes fixed it, because HTTP cache-busting never
    //     touches IndexedDB.
    //   v5 (2026-09-23) — glb_url shape fix. `render_pipeline.glb_file` is a
    //     url-typed metafield and both emission sites in md-configurator.liquid
    //     were missing `.value`, so `| json` serialised the WRAPPER
    //     ({"value":"…","type":"url"}) instead of the string. A cached PPD built
    //     before this fix carries the wrapper object forever — the 3D loader gate
    //     reads it as truthy-but-garbage and would request an [object Object] URL.
    //     Cached vs uncached visitors would also see different PPD shapes.
    //   v6 (2026-09-23) — 3D geometry + finish fields. The composite preprocessor
    //     now stamps `sourceProductGlbUrl` on every merged variant (casings,
    //     entablatures, backing and shelves carry their GLB per member product),
    //     and variants carry `configurator_3d_albedo_url`. Shipped WITHOUT this
    //     bump first, and the operator's own browser kept a pre-fix cached PPD: the
    //     3D door opened as a bare carcass with every composite part missing, while
    //     a fresh visitor would have seen the whole door.
    //   v7 (2026-09-24) — Layer 2 composite clustering stamps
    //     `source_product_glb_url` on every clustered variant. v6 put the member GLB
    //     on the composite-product PREPROCESSOR's output, but the composites the
    //     configurator runs on are built by the data pipeline's Layer 2, so the 3D door
    //     still opened as a bare carcass. The raw product data did not change, so the
    //     content fingerprint cannot see this — it is a processing change.
    //   v8 (2026-09-28) -- Door Depth became a slider category
    //     (CATEGORY_TYPES.SLIDER_CATEGORIES) and Layer 4.6's simplifiedTitle now
    //     trims '5-1/4" Door | 3-1/2" Shelf' to '5-1/4 in.'. Both are baked into
    //     the cached PPD (sliderCategoriesMap, categoryClassification
    //     .usesSliderUI) from UNCHANGED raw data, so without this bump a
    //     returning visitor kept the Door Depth tile grid for up to 7 days.
    //   v9 (2026-09-30) -- Extra Shelves joined SLIDER_CATEGORIES and the
    //     PPD gained categorySliderNotes (metaobject `slider_note`). Both are
    //     baked into the cached PPD, so a returning visitor would otherwise keep
    //     a tile grid and no note for up to 7 days.
    //   v10 (2026-10-08) -- add-on variants carry `variant_glb_url` and
    //     `variant_inset_glb_url` (render_pipeline.glb_file / inset_glb_file on
    //     the VARIANT), which the 3D renderer now prefers over the product GLB so
    //     each knob/pull shows its own model. A pre-v10 cached PPD has neither
    //     field, so every knob would keep rendering the product's one GLB.
    //     Layer 2 composite clustering also stamps `source_product_is_inset`,
    //     so the renderer can tell an inset cabinet door from a standard one.
    //   v11 (2026-10-12) -- Layer 4 resolves the base product's
    //     `lead_time_category` metaobject onto baseProduct.lead_time (unfinished
    //     range replaces production_time; finished range + per-variant
    //     Wood & Finish classification) and adds baseProduct.shipping_transit_days.
    //     A pre-v11 cached PPD has neither, so a returning visitor would keep the
    //     legacy metafield lead time and a transit-less arrival date.
    PPD_CONTRACT_VERSION: 11,

    // =============================================================================
    // apo_style ONLY - categories auto-defaulted once a wood is applied
    // =============================================================================
    // Ryan, UAT 2026-08-27: "When the user chooses their wood species and
    // finish, auto apply the standard front casing, standard back casing,
    // standard front entablature, and standard back entablature for them. They
    // can, of course change this if they want to when they reach that category,
    // but it will be the default applied, just like how we handle the default
    // width and heights."
    //
    // These are seeded on the WOOD event, never at t=0, and that is the whole
    // point: all four carry MATERIAL-AWARE defaults in
    // simplifiedDefaultsByCategory (a by_material_title map keyed Paint Grade /
    // White Oak / Veneers / TFL), so before a wood exists there is no correct
    // variant to pick -- only the fallback, which would be a Paint Grade part
    // silently attached to a customer heading for Walnut.
    //
    // Verified against live PPD data 2026-08-27: every entry in every material
    // family for all four categories resolves to the "Standard <category>"
    // PRODUCT, so honouring the configured default IS honouring Ryan's
    // request. Do not hardcode a variant id or match on the word "Standard" --
    // if the merchant re-points a default in the metaobjects, this list should
    // follow it.
    APO_STYLE_MATERIAL_SEED_CATEGORIES: Object.freeze([
      'Front Casing',
      'Back Casing',
      'Front Entablature',
      'Back Entablature',
      // Ryan, UAT 2026-08-27: "when the user has selected their wood and their
      // finish, auto-select the Standard Shelves for the top section and the
      // bottom section for them."
      //
      // WHICH shelf gets seeded is admin data, not code. These two entries say
      // "seed this category from its configured default"; the default itself
      // lives on the `configurator_tab_categories` metaobjects `top-pocket` and
      // `bottom-pocket` (field `simplified_default_variant_id`), and on
      // 2026-08-27 those were repointed from the LIPPED products (+$45 / +$25)
      // to the STANDARD ones ($0) -- see the metaobject, not this file, if the
      // seeded shelf is ever wrong. Same principle as apo_load_order: the
      // choice belongs in metafields, not in a conditional here.
      'Top Section',
      'Bottom Section',
      // 'Shelf Style' (2026-09-16): the merged single-step replacement for
      // Top/Bottom Section on flush-mount-bookcase-door-2-0. Its own default
      // lives on the new `shelf-style` configurator_tab_categories metaobject's
      // `simplified_default_variant_id` field, mirroring the pattern above.
      // Added alongside 'Top Section'/'Bottom Section' rather than replacing
      // them -- Archway Bookcase Door 2.0 still needs those two seeded.
      'Shelf Style',
      // Ryan, UAT 2026-08-28: pre-select the standard jamb depth the way the
      // other categories pre-select their defaults.
      //
      // Jamb Depth is the first entry here that is NOT material-aware: its
      // metaobject default is a plain variant id (43257109053485 = 4-9/16 in.,
      // $0.00), not a by_material_title map. That works unchanged --
      // resolveDefaultEntry() takes the plain-string branch and never consults
      // the assumed wood -- so the list is really "categories seeded from their
      // configured default in apo_style", and the MATERIAL in the constant name
      // describes the TRIGGER (a wood application), not a requirement on the
      // default's shape.
      //
      // Consequence worth knowing: seeding still happens on that wood trigger,
      // so Jamb Depth is filled once the customer applies Wood & Finish rather
      // than at t=0. That is deliberate -- apo_style has no t=0 seeding path and
      // must not grow one (see isApoMaterialSeed in the coordinator) -- and in
      // practice Wood & Finish is the first category in the apo_style scroll.
      //
      // As always, WHICH jamb depth is admin data: change the
      // `simplified_default_variant_id` on the Jamb Depth category metaobject,
      // not this file.
      'Jamb Depth',
      // Ryan, UAT 2026-08-28: pre-select the zero-cost standard for the
      // remaining categories that carry one, so the customer is not asked to
      // confirm a choice that has no price impact and no alternative default.
      //
      // Live metaobject defaults on 2026-08-28 (all $0.00, all already correct
      // before this change -- only the seeding was missing):
      //   Door Depth             43257064194093  8-1/4 in. (Shelf Depth = 6-1/2 in.)
      //   Door Backing           material-aware  Standard Backing
      //   Locking Systems        43493045141549  Friction Catch Only
      //   Smart Home Automations 44037686165549  No Smart Home Automations
      //   Delivery               44612948033581  No Rush Fee Upgrade
      //
      // 🚨 TIMING. Seeding runs on the wood-applied trigger, never at t=0, so a
      // customer who lands and leaves has applied NOTHING and their URL stays
      // clean -- that is the requirement, and it is why these belong on this
      // list rather than in any load-time path. Verified live: at page load the
      // only applied selections come from ?config= restoration; with no saved
      // config nothing is applied until the customer chooses a wood, which in
      // apo_style is the first category in the scroll.
      //
      // Door Backing is the one material-aware entry here, so it genuinely
      // needs the applied wood to resolve to the right species/finish -- a
      // further reason the trigger stays wood-gated rather than firing on any
      // first click.
      'Door Depth',
      'Door Backing',
      'Locking Systems',
      'Smart Home Automations',
      // 2026-09-30: Extra Shelves (FMD2) is optional in real MageWorx APO
      // ("-- Please Select --"), but every configurator card is required for
      // Add to Cart, so its $0 "No Extra Shelves" product is the configured
      // default on the `extra-shelves` metaobject. Same shape as Smart Home.
      'Extra Shelves',
      'Delivery',
      // Ryan, 2026-09-11: "ship 'Appearance Upgrades' to auto-default to 'No
      // Appearance Upgrades $0' the way Delivery does." Same shape as Delivery:
      // a plain (non-material-aware) $0.00 default --
      // simplified_default_variant_id = "44045476102189" ("No Appearance
      // Upgrades (Single/French Door)") on the `appearance-upgrades` category
      // metaobject. Its product DOES carry a custom_configurator_front_image
      // (the shared fully-transparent placeholder every other "None" option
      // already uses -- see e.g. "No Knobs or Pulls"), so applying it on the
      // wood-applied trigger produces no visible carousel change, exactly like
      // every other zero-cost standard in this list.
      'Appearance Upgrades',
    ]),

    // =============================================================================
    // APO STYLE SHELF MIRROR PAIRS - two real categories, kept in sync
    // =============================================================================
    // apo_style only (Daniel 2026-08-13): "in apo selecting the shelves counts
    // it for both the top and bottom shelves" -- real MageWorx APO has ONE
    // shelf-type control, not two. Top Section and Bottom Section stay two
    // separate, fully real, independently visible/required categories
    // (Daniel 2026-08-13: "the top and bottom section are separate from the
    // cabinets and should still work as such") -- NOT a hide-one-behind-the-
    // other design (that was tried the same day, reverted after it broke Add
    // to Cart). Instead, whichever side the customer picks first, the OTHER
    // side auto-applies the SAME-TITLED variant a moment later
    // (md-config-bl-shelf-mirror-coordinator.js) -- "one card affect the
    // other no matter what," bidirectional, not just top-drives-bottom.
    //
    // Verified live 2026-08-13 (flush-mount-bookcase-door-2-0): every one of
    // Top Section's ~30 distinct variant titles also exists on Bottom Section
    // and vice versa (no title exclusive to either side), so a title-match
    // lookup always finds a target variant. Caveat: a handful of titles
    // (Paint Grade options, White Oak Veneer) have MORE raw variant IDs than
    // expected on one or both sides -- data-hygiene duplicates within a single
    // product, not missing/mismatched options -- so the coordinator takes the
    // first title match, which is accurate in practice (duplicates share the
    // same customer-facing title/price/image).
    //
    // Only the Top/Bottom Section pair is confirmed this way. The
    // 'Top Pocket'/'Bottom Pocket' synonym pair (seen on a different product's
    // fixture data, never live-verified for this same title-overlap property)
    // is deliberately NOT included here -- don't assume it mirrors the same
    // way without checking real data for a product that actually uses it.
    APO_STYLE_SHELF_MIRROR_PAIRS: Object.freeze([
      Object.freeze(['Top Section', 'Bottom Section']),
    ]),

    // =============================================================================
    // CATEGORY TYPES - Special UI rendering modes
    // =============================================================================
    // TODO: Make category types data-driven by adding boolean fields to
    // configurator_tab_categories metaobject:
    //   - is_measurement_category (tape measure icon + "how to measure" text)
    //   - uses_slider_ui (horizontal slider instead of thumbnails)
    // This would decouple UI rendering from hardcoded category names.
    CATEGORY_TYPES: Object.freeze({
      // Measurement categories use tape measure icon + "how to measure" text
      // instead of generic info icon in category tooltips.
      // Values are canonical display names; consumers normalize as needed.
      MEASUREMENT_CATEGORIES: Object.freeze(
        new Set(['Width', 'Height', 'Jamb Depth', 'Door Width', 'Door Height'])
      ),

      // Slider categories use horizontal slider UI instead of thumbnail grid.
      // Referenced by data pipeline (Phase 1) for slider map construction.
      //
      // Door Depth joined 2026-09-28 (owner decision): global, every product
      // with a Door Depth category, French doors included, no opt-outs. Its
      // titles ('8-1/4" Door | 6-1/2" Shelf') parse on the leading number, so
      // the slider sorts 5-1/4, 6-1/2, 8-1/4, 10, 12 and self-seeds its
      // metaobject default (8-1/4" Door, $0) exactly like Width/Height.
      // 'Door Pocket Depth' (the "Door Bookcase" fixture's synonym for the same
      // step, see APO_STYLE order above) is deliberately NOT listed: the owner
      // named Door Depth only.
      // 'Extra Shelves' (2026-09-30): count variants ("3 Extra Shelves") parse
      // on the leading number like the dimensions do, and
      // DERIVED_PRODUCT_RULES narrows the stops to the Shelf Style product.
      SLIDER_CATEGORIES: Object.freeze(
        new Set(['Width', 'Height', 'Door Depth', 'Extra Shelves'])
      ),
    }),

    // =============================================================================
    // NUMERIC INPUT PRODUCTS - PRODUCT-scoped, not category-scoped
    // =============================================================================
    // A sibling to CATEGORY_TYPES above, not nested inside it, because this Set
    // operates at PRODUCT granularity while CATEGORY_TYPES' Sets are all
    // category-granularity. Products listed here render a typed-measurement
    // input card (md-config-ui-numeric-input-selector.js) instead of a
    // variant-tile grid in Phase 1's productToVariantLayoutHTMLMap builder —
    // for a product whose variant count makes a tile grid unusable (Custom
    // Jamb Depth: 133 variants). Keyed on product TITLE, matching the
    // APO_STYLE_SHELF_MIRROR_PAIRS title-keying convention above (survives
    // product-ID drift between environments).
    //
    // TODO: make this metaobject-driven (e.g. a boolean field on the product
    // or a configurator_tab_categories flag) rather than hardcoded here — same
    // TODO philosophy as SLIDER_CATEGORIES above, one granularity level down.
    NUMERIC_INPUT_PRODUCTS: Object.freeze(
      new Set(['Custom Jamb Depth (Single/French Door)'])
    ),

    // =============================================================================
    // FORBIDDEN RULE VOCABULARY - forbidden_rule_unified metaobject field values
    // =============================================================================
    // conflict_direction and target_action are free-text metaobject fields; these
    // are the values the theme understands. ENFORCED_PAIR is only meaningful on an
    // AUTO_APPLY_OPTION rule and is opt-in: it upgrades that rule from "fill the
    // target category if the customer has not touched it" to a hard pairing (force
    // the target over a manual pick, and clear the condition's category when the
    // customer picks a non-target option in the target category). See
    // memory-bank/FORBIDDEN-COMBINATIONS-UNIFIED-SYSTEM-GUIDE.md.
    FORBIDDEN_RULES: Object.freeze({
      TARGET_ACTIONS: Object.freeze({
        DISABLE_VARIANTS: 'DISABLE_VARIANTS',
        AUTO_APPLY_OPTION: 'AUTO_APPLY_OPTION',
      }),
      CONFLICT_DIRECTIONS: Object.freeze({
        BIDIRECTIONAL: 'BIDIRECTIONAL',
        ONE_WAY: 'ONE_WAY',
        MUTUAL_EXCLUSION: 'MUTUAL_EXCLUSION',
        ENFORCED_PAIR: 'ENFORCED_PAIR',
      }),
      ENFORCED_PAIR_ACTIONS: Object.freeze({
        FORCE_APPLY: 'FORCE_APPLY',
        CLEAR_CATEGORY: 'CLEAR_CATEGORY',
      }),
      // Provenance stamped on STATE_SELECTION_CLEARED when an enforced pair clears a category
      ENFORCED_PAIR_CLEAR_SOURCE: 'forbidden_enforced_pair',
    }),

    // =============================================================================
    // EVENT SOURCES - Interaction origin tracking
    // =============================================================================
    EVENT_SOURCES: Object.freeze({
      USER_CLICK: 'user_click', // Manual user interaction (tab/category clicks)
      AUTO_NAVIGATOR: 'auto_navigator', // Programmatic navigation from auto-nav coordinator
      SYSTEM: 'system', // Internal system-triggered navigation
    }),

    // =============================================================================
    // INERT TAP SURFACES - the closed enum for EVENTS.UI_INERT_TAP_DETECTED
    //
    // ONE definition shared by every publisher and by
    // md-config-support-analytics-reporter.js, which forwards the value
    // VERBATIM as `element_label`. That is the whole reason this is a frozen
    // enum rather than a string literal at each call site: element_label must
    // never carry textContent, a catalog title, a price, or anything else a
    // customer typed or the catalog supplied, and a shared closed set is what
    // makes that structurally true instead of merely intended.
    //
    // 🚨 ADDING A SURFACE IS A TAXONOMY CHANGE. Register it in
    // ../murphy-door-super-app/docs/DECLARATIVE_ANALYTICS_TAXONOMY.md
    // (config_inert_tap, label enum) in the SAME PR — an unregistered value
    // lands in store_visits with no definition and nobody can GROUP BY it a
    // year from now.
    // =============================================================================
    INERT_TAP_SURFACES: Object.freeze({
      // The mobile step dot for the step the customer is already on.
      CURRENT_STEP: 'current_step',
      // The live price readout. Looks tappable (it kept .murphy-door-info-icon
      // for layout) but its tooltip initializer was deleted in 9c1c9f84.
      PRICE_DISPLAY: 'price_display',
      // The shipping/delivery estimate readout — same history as the price.
      SHIPPING_ESTIMATE: 'shipping_estimate',
      //
      // 🗑️ REMOVED, and each removal is the SHAPE OF SUCCESS for this enum, not
      // a regression — see the taxonomy doc: "a token going to zero is the
      // expected shape of success here".
      //
      //   card_header   — the apo_style card header now FOCUSES its own card
      //   applied_tile  — a re-tap of the ringed tile now confirms and moves on
      //                   to the next incomplete card (or the buy area)
      //
      //     Both were published PURELY to measure their inertness. They are no
      //     longer inert, so the measurement has nothing left to measure and
      //     the publish sites went with the fix. They now feed `config_nav_tap`
      //     instead (NAV_TAP_SOURCES below), which is a different question:
      //     "how often is this navigation gesture used", not "how often does a
      //     tap do nothing". Do NOT re-add them here to keep historical
      //     continuity — two verbs describing the same tap with opposite
      //     meanings is exactly how a year-old GROUP BY becomes unreadable.
      //
      //   slider_bound  — DROPPED WITHOUT EVER FIRING, deliberately. Those +/-
      //     buttons carry the `disabled` attribute at the bound and a real tap
      //     on a disabled <button> dispatches no click at all, so the publish
      //     site was unreachable by a pointer and the token could only ever
      //     report a permanent ZERO. The greyed-out button (opacity 0.3 /
      //     cursor:not-allowed) is already the customer's feedback that they
      //     have hit the end of the range, so there is nothing to fix and
      //     therefore nothing worth carrying a permanently-zero token for.
    }),

    // =============================================================================
    // NAV TAP SOURCES - the closed enum for `config_nav_tap`'s element_label
    //
    // Which affordance asked the configurator to move the customer somewhere.
    // Read by md-config-support-analytics-reporter.js, which forwards the value
    // VERBATIM as `element_label` and DROPS anything not in this set — the same
    // structural guarantee INERT_TAP_SURFACES above exists to provide, for the
    // same reason: element_label must never be able to carry textContent, a
    // catalog title or a rendered price.
    //
    // 🚨 ADDING A SOURCE IS A TAXONOMY CHANGE. Register it in
    // ../murphy-door-super-app/docs/DECLARATIVE_ANALYTICS_TAXONOMY.md
    // (config_nav_tap, label enum) in the SAME PR.
    // =============================================================================
    // 🚨 SUMMARY_DOT REMOVED 2026-09-21 (issue #1829). The mobile Summary
    // step dot's "jump to the buy area" branch was removed from
    // md-config-ui-mobile-swipe.js's renderStepDots() click handler:
    // .murphy-door-configurator__step-dots is unconditionally display:none
    // in apo_style (assets/section-md-apo-mode.css), so the dot this source
    // described has no visible affordance in that experience and the branch
    // was dead in production. Do NOT re-add it for historical continuity —
    // same reasoning as slider_bound below: a token that can never fire is
    // worse than no token.
    NAV_TAP_SOURCES: Object.freeze({
      // The apo_style category-card header — focuses its own card.
      CARD_HEADER: 'card_header',
      // A re-tap of the already-applied (ringed) tile — "yes, this one".
      APPLIED_TILE: 'applied_tile',
      // The coordinator's answer to a confirm-and-continue: whichever card (or
      // the buy area) the customer was sent to. Distinct from applied_tile,
      // which records the GESTURE; this records the DESTINATION being reached.
      CONFIRM_CONTINUE: 'confirm_continue',
    }),

    // =============================================================================
    // CATEGORY HEADER - the configurator card's accordion toggle (WCAG 4.1.2)
    //
    // Each category card's header is a WAI-ARIA APG accordion header: a
    // role="heading" span holding a native <button aria-expanded aria-controls>
    // that contains ONLY the category title. The card div itself is a plain
    // container. Built in md-config-ui-navigation-manager.js
    // generateCategoryHTML(); read by md-config-core-event-handler.js (apo_style
    // header-tap guard) and md-config-ui-updater.js (focus + describedby).
    // One definition so the three modules cannot drift apart on the selector.
    // =============================================================================
    CATEGORY_HEADER: Object.freeze({
      TOGGLE_CLASS: 'murphy-door-configurator__category-toggle',
      TOGGLE_SELECTOR: '.murphy-door-configurator__category-toggle',
      HEADING_CLASS: 'murphy-door-configurator__category-heading',
      // The configurator's only other heading is the product <h1>.
      HEADING_LEVEL: '2',
      // Prefix for the toggle / panel / summary ids that wire aria-controls
      // and aria-describedby.
      ID_PREFIX: 'md-cat-',
    }),

    // =============================================================================
    // CATEGORY PICTOGRAMS - which category headers carry a live 1:1 pictogram
    //
    // categoryId -> pictogram KIND. md-config-ui-navigation-manager.js renders
    // an empty, aria-hidden slot in the header of every category listed here;
    // md-config-ui-category-pictograms.js owns the kinds (geometry + state
    // reader per kind, in its PICTOGRAMS config) and fills the slot. Giving
    // another category a pictogram is a line here, plus a kind there if none
    // of the existing ones fits.
    //
    // 'Door Pocket Depth' is the "Door Bookcase" fixture's synonym for Door
    // Depth (see APO_STYLE order above); it reads the same two-part title.
    // =============================================================================
    CATEGORY_PICTOGRAMS: Object.freeze({
      'Door Depth': 'door-depth',
      'Door Pocket Depth': 'door-depth',
      'Shelf Style': 'shelf-style',
    }),

    // =============================================================================
    // READY PROMPT - the closed enums for `config_ready_prompt`
    //
    // The "Your door is ready!" sticky badge (md-config-ui-ready-badge.js)
    // publishes EVENTS.UI_READY_PROMPT_INTERACTION with an `action` from
    // READY_PROMPT_ACTIONS and a `context` from READY_PROMPT_CONTEXTS. The
    // reporter forwards `action` VERBATIM as `element_label` and `context` as
    // `element_position`, and DROPS anything outside these sets -- the same
    // structural free-text guarantee INERT_TAP_SURFACES / NAV_TAP_SOURCES
    // provide, for the same reason.
    //
    // 🚨 ADDING A TOKEN IS A TAXONOMY CHANGE. Register it in
    // ../murphy-door-super-app/docs/DECLARATIVE_ANALYTICS_TAXONOMY.md
    // (config_ready_prompt) in the SAME PR.
    // =============================================================================
    READY_PROMPT_ACTIONS: Object.freeze({
      // The badge became visible.
      SHOWN: 'shown',
      // The customer pressed the X.
      DISMISS: 'dismiss',
      // The customer pressed "Keep personalizing".
      KEEP: 'keep',
      // The customer pressed the badge's own Add to Cart.
      ATC: 'atc',
      // The badge hid itself with no customer gesture on it.
      AUTO_HIDE: 'auto_hide',
    }),
    READY_PROMPT_CONTEXTS: Object.freeze({
      // `shown`: the door was already complete when the page finished
      // bootstrapping (shared ?config= link, returning visitor, seeded
      // defaults) -- the customer did not complete it in this session.
      BOOTSTRAP: 'bootstrap',
      // `shown`: the customer's own selection took the door to 100%.
      LIVE: 'live',
      // `auto_hide`: the add to cart succeeded.
      CART_ADDED: 'cart_added',
      // `auto_hide`: a selection change dropped the door below 100%.
      INCOMPLETE: 'incomplete',
    }),

    // =============================================================================
    // EVENT NAMES - Organized by namespace (486 total events)
    // =============================================================================
    EVENTS: Object.freeze({
      // =========================================================================
      // BUSINESS EVENTS (business:*) - 82 events
      // Business logic lifecycle, cart operations, material selection, pricing
      // =========================================================================
      BUSINESS_APPLIED_SELECTIONS_PRICE_RETRIEVED:
        'business:applied_selections_price_retrieved',
      BUSINESS_AUTO_NAVIGATION_COORDINATOR_CLEANUP:
        'business:auto_navigation_coordinator_cleanup',
      BUSINESS_AUTO_NAVIGATION_COORDINATOR_READY:
        'business:auto_navigation_coordinator_ready',
      BUSINESS_AUTO_NAVIGATION_FAILED: 'business:auto_navigation_failed',
      BUSINESS_AUTO_NAVIGATION_PROGRESS: 'business:auto_navigation_progress',
      BUSINESS_AUTO_NAVIGATION_SKIPPED: 'business:auto_navigation_skipped',
      BUSINESS_AUTO_NAVIGATION_START: 'business:auto_navigation_start',
      BUSINESS_AUTO_NAVIGATION_SUCCESS: 'business:auto_navigation_success',
      BUSINESS_AUTO_NAVIGATION_VALIDATION:
        'business:auto_navigation_validation',
      BUSINESS_BASE_PRICE_RETRIEVED: 'business:base_price_retrieved',
      BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED:
        'business:cascade_auto_selection_completed',
      BUSINESS_CASCADE_AUTO_SELECTION_FAILED:
        'business:cascade_auto_selection_failed',
      BUSINESS_CASCADE_AUTO_SELECTION_SKIPPED:
        'business:cascade_auto_selection_skipped',
      BUSINESS_CASCADE_AUTO_SELECTION_STARTED:
        'business:cascade_auto_selection_started',
      BUSINESS_CASCADE_AUTO_SELECTION_STEP:
        'business:cascade_auto_selection_step',
      BUSINESS_CASCADE_APPLY_URL_SELECTIONS:
        'business:cascade_apply_url_selections',
      BUSINESS_CASCADE_APPLY_WILLITFIT_SELECTIONS:
        'business:cascade_apply_willitfit_selections',
      BUSINESS_CASCADE_APPLY_SIMPLIFIED_DEFAULTS:
        'business:cascade_apply_simplified_defaults',
      BUSINESS_CASCADE_APPLY_CURATED_DEFAULTS:
        'business:cascade_apply_curated_defaults',
      // A requestId / build_preset caller's apply request was refused BEFORE a
      // cascade started (coordinator busy, empty batch). Deliberately NOT
      // BUSINESS_CASCADE_AUTO_SELECTION_FAILED: cart-manager, ui-updater and
      // others treat any FAILED as end-of-cascade and release their ATC gates,
      // so a refusal during someone else's cascade must never reach them.
      // Payload: { requestId, context, reason, retryable, source }.
      BUSINESS_CASCADE_APPLY_REQUEST_REJECTED:
        'business:cascade_apply_request_rejected',
      BUSINESS_CURATED_DEFAULTS_APPLIED: 'business:curated_defaults_applied',
      // "Build this" preset lifecycle (md-config-support-build-preset.js).
      // Payloads carry { requestId, handle }; see architecture.md.
      BUSINESS_BUILD_PRESET_REQUESTED: 'business:build_preset_requested',
      BUSINESS_BUILD_PRESET_QUEUED: 'business:build_preset_queued',
      BUSINESS_BUILD_PRESET_APPLIED: 'business:build_preset_applied',
      BUSINESS_BUILD_PRESET_PARTIAL: 'business:build_preset_partial',
      BUSINESS_BUILD_PRESET_FAILED: 'business:build_preset_failed',
      // The apply would overwrite a selection the customer made by hand, so
      // the UI module asks first (md-config-ui-build-preset.js dialog) and
      // answers on UI_BUILD_PRESET_CONFIRM_RESOLVED. Payload { requestId,
      // handle, origin, overwriteCategories, unavailableCategories }.
      BUSINESS_BUILD_PRESET_CONFIRM_REQUESTED:
        'business:build_preset_confirm_requested',
      BUSINESS_BUILD_PRESET_CLEARED: 'business:build_preset_cleared',
      // The customer changed a preset category by hand: the cart line gets
      // _Build_Preset_Edited.
      BUSINESS_BUILD_PRESET_EDITED: 'business:build_preset_edited',
      // Analytics only: EVERY manual pick on a preset category that differs
      // from the applied pick, one per pick (EDITED above stays one-shot
      // because it sets the cart property). Payload { handle, requestId,
      // categoryId }. The analytics reporter dedupes it per category per
      // preset session into build_this_edited.
      BUSINESS_BUILD_PRESET_CATEGORY_EDITED:
        'business:build_preset_category_edited',
      BUSINESS_BUILD_PRESET_SLIDE_INVALID: 'business:build_preset_slide_invalid',
      BUSINESS_CURATED_REDEFAULT_COMPLETED:
        'business:curated_redefault_completed',
      BUSINESS_CONFIGURATOR_MODE_CHANGE_REQUESTED:
        'business:configurator_mode_change_requested',
      BUSINESS_CONFIGURATOR_MODE_CHANGED: 'business:configurator_mode_changed',
      BUSINESS_GATE_ANSWER_CHANGE_REQUESTED:
        'business:gate_answer_change_requested',
      BUSINESS_GATE_ANSWER_CHANGED: 'business:gate_answer_changed',
      // 🚨 A REAL CLICK ON THE ANSWER THE GATE ALREADY HOLDS.
      //
      // _setGateAnswer early-returns without publishing
      // BUSINESS_GATE_ANSWER_CHANGED when the answer it is handed matches the
      // one already stored -- correctly, because nothing CHANGED and every
      // subscriber of that event (visibility manager, auto-navigation,
      // config_gate_answered in the analytics reporter) would re-run for a
      // non-event. But a re-affirmation is still the only signal the customer
      // can produce when the gate's own auto-applied fallbacks are MISSING
      // from the applied-selections map, which is exactly the dead end in
      // issue #1826: answer 'none' is stored, the four categories it hides are
      // hidden AND unapplied, progress is stuck below 100%, and clicking None
      // again published nothing at all.
      //
      // Deliberately a SEPARATE event rather than relaxing the early return:
      // it has exactly one subscriber (md-config-bl-gate-card-coordinator.js's
      // reconcileGateFallbacks) and "the customer re-asserted this answer" is
      // a different fact from "this answer changed".
      BUSINESS_GATE_ANSWER_REAFFIRMED: 'business:gate_answer_reaffirmed',
      BUSINESS_CASCADE_APPLY_GATE_DEFAULTS:
        'business:cascade_apply_gate_defaults',
      UI_GATE_DEPENDENT_CATEGORIES_VISIBILITY_UPDATED:
        'ui:gate_dependent_categories_visibility_updated',
      UI_GATE_OPEN_REQUEST: 'ui:gate_open_request',
      UI_GATE_OPENED: 'ui:gate_opened',
      UI_GATE_REOPEN_REQUESTED: 'ui:gate_reopen_requested',
      // Published when a reopened-for-editing gate gets force-closed by a
      // REAL category opening (not by the user picking a new gate answer) --
      // e.g. reopen Swing, then open Door Depth. The gate's stored answer
      // never changed, so its answered/collapsed visual state must be
      // restored, not left in the reopened/unanswered look. Distinct from
      // UI_GATE_REOPEN_REQUESTED (opposite direction) and
      // BUSINESS_GATE_ANSWER_CHANGED (a real new pick). Live-reproduced
      // 2026-08-13 (Daniel): swing gate turned visually blank and
      // navigation jumped back to the Cabinets gate after this sequence.
      UI_GATE_FORCE_CLOSED: 'ui:gate_force_closed',
      BUSINESS_SIMPLIFIED_MODE_CATEGORIES_READY:
        'business:simplified_mode_categories_ready',
      UI_SIMPLIFIED_BUILD_MY_DOOR_CLICKED:
        'ui:simplified_build_my_door_clicked',
      BUSINESS_SIMPLIFIED_MODE_COMPLETE: 'business:simplified_mode_complete',
      BUSINESS_SIMPLIFIED_MODE_ESCAPED: 'business:simplified_mode_escaped',
      // A simplified-mode funnel FACT ('started' | 'step_completed' |
      // 'auto_applied'). The coordinator publishes it; the analytics reporter
      // is the only subscriber and owns the transmission. Kept event-driven
      // rather than letting the BL module publish to Shopify.analytics itself,
      // so simplified-mode inherits the reporter's consent buffer and sample
      // rate instead of re-implementing (and, as shipped, mis-implementing)
      // them. See md-config-support-analytics-reporter.js.
      BUSINESS_SIMPLIFIED_MILESTONE_REACHED:
        'business:simplified_milestone_reached',
      BUSINESS_BROWSING_COMPOSITE_CONTEXT_RESOLVED:
        'business:browsing_composite_context_resolved',
      BUSINESS_BROWSING_CONTEXT_VALIDATION_COMPLETED:
        'business:browsing_context_validation_completed',
      BUSINESS_BROWSING_CONTEXT_VALIDATION_FAILED:
        'business:browsing_context_validation_failed',
      BUSINESS_BROWSING_CONTEXT_VALIDATION_STARTED:
        'business:browsing_context_validation_started',
      BUSINESS_BROWSING_PAYLOAD_STANDARDIZATION_COMPLETED:
        'business:browsing_payload_standardization_completed',
      BUSINESS_BROWSING_PRODUCT_TYPE_DETECTED:
        'business:browsing_product_type_detected',
      BUSINESS_BROWSING_STATE_APPLIED: 'business:browsing_state_applied',
      BUSINESS_BROWSING_STATE_COORDINATION_COMPLETED:
        'business:browsing_state_coordination_completed',
      BUSINESS_BROWSING_STATE_COORDINATION_FAILED:
        'business:browsing_state_coordination_failed',
      BUSINESS_BROWSING_STATE_COORDINATION_STARTED:
        'business:browsing_state_coordination_started',
      BUSINESS_BROWSING_STATE_DELEGATION_COMPLETED:
        'business:browsing_state_delegation_completed',
      BUSINESS_BROWSING_STATE_FAILED: 'business:browsing_state_failed',
      BUSINESS_BROWSING_STATE_STARTED: 'business:browsing_state_started',
      BUSINESS_CART_ADD_COMPLETED: 'business:cart_add_completed',
      BUSINESS_CART_ADD_FAILED: 'business:cart_add_failed',
      // An Add to Cart refused because a required category is not APPLIED
      // (conductor.getCartReadiness). Deliberately NOT BUSINESS_CART_ADD_FAILED:
      // that one raises the cart error modal, and this is an unfinished door,
      // not a failure. The customer is sent to the category instead.
      BUSINESS_CART_ADD_REFUSED_INCOMPLETE:
        'business:cart_add_refused_incomplete',
      BUSINESS_CART_ADD_STARTED: 'business:cart_add_started',
      // A second Add to Cart tap while an add is already in flight, ignored so
      // it cannot POST a second door. Published so the swallow is never silent.
      BUSINESS_CART_ADD_DUPLICATE_IGNORED:
        'business:cart_add_duplicate_ignored',
      BUSINESS_CART_ATTRIBUTES_UPDATE_COMPLETED:
        'business:cart_attributes_update_completed',
      BUSINESS_CART_ATTRIBUTES_UPDATE_FAILED:
        'business:cart_attributes_update_failed',
      BUSINESS_CART_ATTRIBUTES_UPDATE_STARTED:
        'business:cart_attributes_update_started',
      BUSINESS_CART_BACKGROUND_PREP_COMPLETED:
        'business:cart_background_prep_completed',
      BUSINESS_CART_BACKGROUND_PREP_FAILED:
        'business:cart_background_prep_failed',
      BUSINESS_CART_BACKGROUND_PREP_STARTED:
        'business:cart_background_prep_started',
      BUSINESS_CART_CACHE_INVALIDATED: 'business:cart_cache_invalidated',
      BUSINESS_CART_CACHE_WARMED: 'business:cart_cache_warmed',
      BUSINESS_CART_FINISH_INCONSISTENCY_DETECTED:
        'business:cart_finish_inconsistency_detected',
      BUSINESS_CART_PREPARATION_COMPLETED:
        'business:cart_preparation_completed',
      BUSINESS_CART_PREPARATION_STARTED: 'business:cart_preparation_started',
      BUSINESS_CART_PRICE_CALC_COMPLETED: 'business:cart_price_calc_completed',
      BUSINESS_CART_PRICE_CALC_FAILED: 'business:cart_price_calc_failed',
      BUSINESS_CART_PRICE_CALC_STARTED: 'business:cart_price_calc_started',
      BUSINESS_CART_UPDATED: 'business:cart_updated',
      BUSINESS_FRACTION_CONVERSION_COMPLETED:
        'business:fraction_conversion_completed',
      BUSINESS_GROUP_ANALYSIS_COMPLETED: 'business:group_analysis_completed',
      BUSINESS_GROUP_ANALYSIS_FAILED: 'business:group_analysis_failed',
      BUSINESS_GROUP_ANALYSIS_STARTED: 'business:group_analysis_started',
      BUSINESS_GROUPED_PRODUCT_SELECTION_COMPLETED:
        'business:grouped_product_selection_completed',
      BUSINESS_GROUPED_PRODUCT_SELECTION_FAILED:
        'business:grouped_product_selection_failed',
      BUSINESS_GROUPED_PRODUCT_SELECTION_STARTED:
        'business:grouped_product_selection_started',
      BUSINESS_GROUPED_PRODUCT_SELECTION_VALIDATION:
        'business:grouped_product_selection_validation',
      BUSINESS_IMAGE_LAYER_OPERATION_COMPLETED:
        'business:image_layer_operation_completed',
      BUSINESS_IMAGE_LAYER_OPERATION_FAILED:
        'business:image_layer_operation_failed',
      BUSINESS_IMAGE_LAYER_OPERATION_PROGRESS:
        'business:image_layer_operation_progress',
      BUSINESS_IMAGE_LAYER_OPERATION_STARTED:
        'business:image_layer_operation_started',
      BUSINESS_IMAGE_LAYER_OPERATION_VALIDATION:
        'business:image_layer_operation_validation',
      BUSINESS_IMAGE_LAYER_SUPPRESSED: 'business:image_layer_suppressed',
      BUSINESS_IMPLICIT_APPLY_COMPLETED: 'business:implicit_apply_completed',
      BUSINESS_IMPLICIT_APPLY_COORDINATION_FAILED:
        'business:implicit_apply_coordination_failed',
      BUSINESS_IMPLICIT_APPLY_COORDINATION_PROGRESS:
        'business:implicit_apply_coordination_progress',
      BUSINESS_IMPLICIT_APPLY_COORDINATION_START:
        'business:implicit_apply_coordination_start',
      BUSINESS_IMPLICIT_APPLY_COORDINATION_SUCCESS:
        'business:implicit_apply_coordination_success',
      BUSINESS_IMPLICIT_APPLY_COORDINATION_VALIDATION:
        'business:implicit_apply_coordination_validation',
      BUSINESS_IMPLICIT_APPLY_FAILED: 'business:implicit_apply_failed',
      BUSINESS_IMPLICIT_APPLY_SKIPPED: 'business:implicit_apply_skipped',
      BUSINESS_IMPLICIT_APPLY_STARTED: 'business:implicit_apply_started',
      BUSINESS_MATERIAL_COMBINATION_INVALID:
        'business:material_combination_invalid',
      BUSINESS_MATERIAL_COMBINATION_VALID:
        'business:material_combination_valid',
      BUSINESS_MATERIAL_FILTER_COMPLETED: 'business:material_filter_completed',
      BUSINESS_MATERIAL_SCORING_COMPLETED:
        'business:material_scoring_completed',
      BUSINESS_MATERIAL_SCORING_FAILED: 'business:material_scoring_failed',
      BUSINESS_MATERIAL_SCORING_STARTED: 'business:material_scoring_started',
      BUSINESS_MATERIAL_SELECTION_COMPLETED:
        'business:material_selection_completed',
      BUSINESS_MATERIAL_SELECTION_FAILED: 'business:material_selection_failed',
      BUSINESS_MATERIAL_SELECTION_PROGRESS:
        'business:material_selection_progress',
      BUSINESS_MATERIAL_SELECTION_STARTED:
        'business:material_selection_started',
      BUSINESS_MATERIAL_SELECTION_VALIDATION:
        'business:material_selection_validation',
      BUSINESS_PRICE_CALCULATION_COMPLETED:
        'business:price_calculation_completed',
      BUSINESS_PRICE_CALCULATION_FAILED: 'business:price_calculation_failed',
      BUSINESS_PRICE_CALCULATION_PROGRESS:
        'business:price_calculation_progress',
      BUSINESS_PRICE_CALCULATION_STARTED: 'business:price_calculation_started',
      BUSINESS_PRICE_CALCULATION_VALIDATION:
        'business:price_calculation_validation',
      BUSINESS_SELECTION_PRICE_CALCULATED:
        'business:selection_price_calculated',
      BUSINESS_SHIPPING_CALCULATED: 'business:shipping_calculated',
      BUSINESS_SHIPPING_DELIVERY_DISPLAY_UPDATED:
        'business:shipping_delivery_display_updated',
      BUSINESS_SHIPPING_PROGRESS: 'business:shipping_progress',
      BUSINESS_SHIPPING_STARTED: 'business:shipping_started',
      BUSINESS_SHIPPING_VALIDATION: 'business:shipping_validation',
      BUSINESS_SINGLE_VARIANT_DETECTED: 'business:single_variant_detected',
      BUSINESS_SPACE_CALCULATION_COMPLETED:
        'business:space_calculation_completed',
      BUSINESS_SPACE_CALCULATION_FAILED: 'business:space_calculation_failed',
      BUSINESS_SPACE_CALCULATION_PROGRESS:
        'business:space_calculation_progress',
      BUSINESS_SPACE_CALCULATION_READINESS:
        'business:space_calculation_readiness',
      BUSINESS_SPACE_CALCULATION_STARTED: 'business:space_calculation_started',
      BUSINESS_SPACE_CALCULATION_VALIDATION_FAILED:
        'business:space_calculation_validation_failed',
      BUSINESS_TOTAL_PRICE_CALCULATED: 'business:total_price_calculated',

      // =========================================================================
      // CONDUCTOR EVENTS (conductor:*) - 9 events
      // Central conductor delegation and coordination
      // =========================================================================
      CONDUCTOR_INITIALIZATION_START: 'conductor:initialization_start',
      CONDUCTOR_INITIALIZATION_VALIDATION:
        'conductor:initialization_validation',
      CONDUCTOR_INITIALIZATION_PROGRESS: 'conductor:initialization_progress',
      CONDUCTOR_INITIALIZATION_SUCCESS: 'conductor:initialization_success',
      CONDUCTOR_INITIALIZATION_FAILED: 'conductor:initialization_failed',
      CONDUCTOR_CLEANUP: 'conductor:cleanup',
      CONDUCTOR_MATERIAL_PREFERENCES_COMPLETED:
        'conductor:material_preferences_completed',
      CONDUCTOR_MATERIAL_PREFERENCES_FAILED:
        'conductor:material_preferences_failed',
      CONDUCTOR_MATERIAL_PREFERENCES_REQUESTED:
        'conductor:material_preferences_requested',

      // =========================================================================
      // DEBUG EVENTS (debug:*) - 1 event
      // Debug-level diagnostic events for development observability
      // =========================================================================
      DEBUG_MATERIAL_SELECTION_COMPLETED: 'debug:material_selection_completed',

      // =========================================================================
      // DATA EVENTS (data:*) - 43 events
      // Data requests, responses, and pipeline operations
      // =========================================================================
      DATA_APPLIED_SELECTIONS_RESPONSE: 'data:applied_selections_response',
      DATA_BASE_PRODUCT_VALIDATION_REQUESTED:
        'data:base_product_validation_requested',
      DATA_BASE_PRODUCT_VALIDATION_RESPONSE:
        'data:base_product_validation_response',
      DATA_CART_ITEM_TEMPLATES_REQUESTED: 'data:cart_item_templates_requested',
      DATA_CART_ITEM_TEMPLATES_RESPONSE: 'data:cart_item_templates_response',
      DATA_CART_MUTATIONS_RESPONSE: 'data:cart_mutations_response',
      DATA_CATEGORY_ITEMS_REQUESTED: 'data:category_items_requested',
      DATA_CATEGORY_ITEMS_RESPONSE: 'data:category_items_response',
      DATA_COMPOSITE_GROUPING_COMPLETED: 'data:composite_grouping_completed',
      DATA_COMPOSITE_PREPROCESSING_COMPLETED:
        'data:composite_preprocessing_completed',
      DATA_COMPOSITE_PREPROCESSING_EMPTY_INPUT:
        'data:composite_preprocessing_empty_input',
      DATA_COMPOSITE_PREPROCESSING_FAILED:
        'data:composite_preprocessing_failed',
      DATA_COMPOSITE_PREPROCESSING_FALLBACK:
        'data:composite_preprocessing_fallback',
      DATA_COMPOSITE_PREPROCESSING_STARTED:
        'data:composite_preprocessing_started',
      DATA_COMPOSITE_PRODUCT_CREATED: 'data:composite_product_created',
      DATA_FORBIDDEN_RULES: 'data:forbidden_rules',
      DATA_FORBIDDEN_RULES_RESPONSE: 'data:forbidden_rules_response',
      DATA_FORBIDDEN_STATES_REQUESTED: 'data:forbidden_states_requested',
      DATA_FORBIDDEN_STATES_RESPONSE: 'data:forbidden_states_response',
      DATA_FORBIDDEN_STATES_UPDATE_RESPONSE:
        'data:forbidden_states_update_response',
      DATA_METAOBJECT_LOADED: 'data:metaobject_loaded',
      DATA_PERFORMANCE_METRICS_INCREMENT: 'data:performance_metrics_increment',
      DATA_PERFORMANCE_METRICS_UPDATE: 'data:performance_metrics_update',
      DATA_PREEMPTIVE_FORBIDDEN_REQUEST: 'data:preemptive_forbidden_request',
      DATA_PREEMPTIVE_FORBIDDEN_RESPONSE: 'data:preemptive_forbidden_response',
      DATA_PRODUCT_BY_ID_REQUESTED: 'data:product_by_id_requested',
      DATA_PRODUCT_BY_ID_RESPONSE: 'data:product_by_id_response',
      DATA_PRODUCT_BY_VARIANT_REQUESTED: 'data:product_by_variant_requested',
      DATA_PRODUCT_BY_VARIANT_RESPONSE: 'data:product_by_variant_response',
      DATA_PRODUCT_DATA_RESPONSE: 'data:product_data_response',
      DATA_REQUEST_APPLIED_SELECTIONS: 'data:request_applied_selections',
      DATA_REQUEST_CART_MUTATIONS: 'data:request_cart_mutations',
      DATA_REQUEST_FORBIDDEN_RULES: 'data:request_forbidden_rules',
      DATA_REQUEST_FORBIDDEN_STATES_UPDATE:
        'data:request_forbidden_states_update',
      DATA_REQUEST_PERFECT_PRODUCT_DICTIONARY:
        'data:request_perfect_product_dictionary',
      DATA_REQUEST_PRODUCT_STRUCTURE: 'data:request_product_structure',
      DATA_SYNC_METRICS_INCREMENT: 'data:sync_metrics_increment',
      DATA_SYNC_METRICS_UPDATE: 'data:sync_metrics_update',
      DATA_SYNC_TIMER_REQUESTED: 'data:sync_timer_requested',
      DATA_VALIDATION_COMPLETED: 'data:validation_completed',
      DATA_VALIDATION_CONFLICT_CHECKING_STARTED:
        'data:validation_conflict_checking_started',
      DATA_VALIDATION_CONFLICT_DETECTED: 'data:validation_conflict_detected',
      DATA_VALIDATION_FAILED: 'data:validation_failed',
      DATA_VALIDATION_RULES_RETRIEVED: 'data:validation_rules_retrieved',
      DATA_VALIDATION_STARTED: 'data:validation_started',
      DATA_VARIANT_BY_ID_REQUESTED: 'data:variant_by_id_requested',
      DATA_VARIANT_BY_ID_RESPONSE: 'data:variant_by_id_response',

      // =========================================================================
      // ERROR EVENTS (error:*) - 27 events
      // Error reporting and failure notifications
      // =========================================================================
      ERROR_CART_ADD_FAILED: 'error:cart_add_failed',
      ERROR_CART_PREPARATION_FAILED: 'error:cart_preparation_failed',
      ERROR_COMPLETION_CHECK_FAILED: 'error:completion_check_failed',
      ERROR_DESERIALIZE_STATE_FAILED: 'error:deserialize_state_failed',
      ERROR_EVENT_HANDLER: 'error:event_handler',
      ERROR_METAOBJECT_LOAD_FAILED: 'error:metaobject_load_failed',
      ERROR_PERSISTENCE_RECOVERY_LOAD_FAILED:
        'error:persistence_recovery_load_failed',
      ERROR_PERSISTENCE_RECOVERY_SAVE_FAILED:
        'error:persistence_recovery_save_failed',
      ERROR_PERSISTENCE_SESSION_LOAD_FAILED:
        'error:persistence_session_load_failed',
      ERROR_PERSISTENCE_SESSION_SAVE_FAILED:
        'error:persistence_session_save_failed',
      ERROR_PERSISTENCE_STATE_RECOVERY_FAILED:
        'error:persistence_state_recovery_failed',
      ERROR_PROGRESS_CALCULATION_FAILED: 'error:progress_calculation_failed',
      ERROR_SERIALIZE_STATE_FAILED: 'error:serialize_state_failed',
      ERROR_SHIPPING_CALCULATION: 'error:shipping_calculation',
      ERROR_SHIPPING_DATA_MISSING: 'error:shipping_data_missing',
      ERROR_SPACE_CALCULATION: 'error:space_calculation',
      ERROR_STATE_CHANGE: 'error:state_change',
      ERROR_SUMMARY_CHECK_FAILED: 'error:summary_check_failed',
      ERROR_VALIDATION: 'error:validation',

      // Error Handler Module lifecycle events
      ERROR_HANDLER_DISPLAY_STARTED: 'error:handler_display_started',
      ERROR_HANDLER_DISPLAY_VALIDATION: 'error:handler_display_validation',
      ERROR_HANDLER_DISPLAY_COMPLETED: 'error:handler_display_completed',
      ERROR_HANDLER_DISPLAY_FAILED: 'error:handler_display_failed',
      ERROR_HANDLER_RECOVERY_STARTED: 'error:handler_recovery_started',
      ERROR_HANDLER_RECOVERY_COMPLETED: 'error:handler_recovery_completed',
      ERROR_HANDLER_RECOVERY_FAILED: 'error:handler_recovery_failed',
      ERROR_HANDLER_INITIALIZED: 'error:handler_initialized',

      // =========================================================================
      // FORBIDDEN EVENTS (forbidden:*) - 14 events
      // Forbidden combination detection, resolution, and auto-apply coordination
      // =========================================================================
      FORBIDDEN_AUTO_APPLY_COMPLETED: 'forbidden:auto_apply_completed',
      FORBIDDEN_AUTO_APPLY_FAILED: 'forbidden:auto_apply_failed',
      FORBIDDEN_AUTO_APPLY_TRIGGERED: 'forbidden:auto_apply_triggered',
      FORBIDDEN_AUTO_UNAPPLY_COMPLETED: 'forbidden:auto_unapply_completed',
      FORBIDDEN_AUTO_UNAPPLY_FAILED: 'forbidden:auto_unapply_failed',
      FORBIDDEN_AUTO_UNAPPLY_TRIGGERED: 'forbidden:auto_unapply_triggered',
      // ENFORCED_PAIR reverse direction: clear the condition's category because the
      // customer picked a non-target option in the rule's target category.
      FORBIDDEN_ENFORCED_PAIR_CLEAR_TRIGGERED:
        'forbidden:enforced_pair_clear_triggered',
      FORBIDDEN_ENFORCED_PAIR_CLEAR_COMPLETED:
        'forbidden:enforced_pair_clear_completed',
      FORBIDDEN_ENFORCED_PAIR_CLEAR_FAILED:
        'forbidden:enforced_pair_clear_failed',
      // Add to Cart refused: an enforced pair's condition is applied while its
      // target category holds a non-target option (conductor ATC guard).
      FORBIDDEN_ENFORCED_PAIR_ATC_BLOCKED: 'forbidden:enforced_pair_atc_blocked',
      FORBIDDEN_CONFLICT_DETECTED: 'forbidden:conflict_detected',
      FORBIDDEN_CONFLICT_RESOLVED: 'forbidden:conflict_resolved',
      FORBIDDEN_VALIDATION_COMPLETED: 'forbidden:validation_completed',
      FORBIDDEN_VALIDATION_STARTED: 'forbidden:validation_started',

      // =========================================================================
      // MATERIAL EVENTS (material:*) - 10 events
      // Material preference application, clearing, and 5-stage lifecycle
      // =========================================================================
      MATERIAL_FILTER_STATE_UPDATED: 'material:filter_state_updated',
      MATERIAL_PREFERENCE_APPLIED: 'material:preference_applied',
      MATERIAL_PREFERENCE_APPLY_COMPLETED:
        'material:preference_apply_completed',
      MATERIAL_PREFERENCE_APPLY_FAILED: 'material:preference_apply_failed',
      MATERIAL_PREFERENCE_APPLY_PROGRESS: 'material:preference_apply_progress',
      MATERIAL_PREFERENCE_APPLY_STARTED: 'material:preference_apply_started',
      MATERIAL_PREFERENCE_APPLY_VALIDATION:
        'material:preference_apply_validation',
      MATERIAL_PREFERENCE_CLEARED: 'material:preference_cleared',
      MATERIAL_PREFERENCE_UNAPPLY_FAILED: 'material:preference_unapply_failed',
      MATERIAL_PREFERENCES_RECOMPUTED_FROM_APPLIED:
        'material:preferences_recomputed_from_applied',

      // =========================================================================
      // MODULE EVENTS (module:*) - 34 events
      // Module lifecycle coordination
      // =========================================================================
      MODULE_CART_MANAGER_CLEANUP: 'module:cart_manager_cleanup',
      MODULE_CART_MANAGER_READY: 'module:cart_manager_ready',
      MODULE_IMAGE_LAYER_COORDINATOR_CLEANUP:
        'module:image_layer_coordinator_cleanup',
      MODULE_IMAGE_LAYER_COORDINATOR_READY:
        'module:image_layer_coordinator_ready',
      MODULE_MATERIAL_SELECTOR_CLEANUP: 'module:material_selector_cleanup',
      MODULE_MATERIAL_SELECTOR_READY: 'module:material_selector_ready',
      MODULE_PRICE_CALCULATOR_CLEANUP: 'module:price_calculator_cleanup',
      MODULE_PRICE_CALCULATOR_READY: 'module:price_calculator_ready',
      MODULE_SELECTION_COORDINATOR_CLEANUP:
        'module:selection_coordinator_cleanup',
      MODULE_SELECTION_COORDINATOR_READY: 'module:selection_coordinator_ready',
      MODULE_SHIPPING_CALCULATOR_CLEANUP: 'module:shipping_calculator_cleanup',
      MODULE_SHIPPING_CALCULATOR_READY: 'module:shipping_calculator_ready',
      MODULE_MOBILE_SWIPE_CLEANUP: 'module:mobile_swipe_cleanup',
      MODULE_MOBILE_SWIPE_READY: 'module:mobile_swipe_ready',
      MODULE_ANALYTICS_REPORTER_CLEANUP: 'module:analytics_reporter_cleanup',
      MODULE_ANALYTICS_REPORTER_READY: 'module:analytics_reporter_ready',
      MODULE_FORBIDDEN_VALIDATOR_CLEANUP: 'module:forbidden_validator_cleanup',
      MODULE_FORBIDDEN_VALIDATOR_READY: 'module:forbidden_validator_ready',
      MODULE_GROUPED_PRODUCT_HANDLER_CLEANUP:
        'module:grouped_product_handler_cleanup',
      MODULE_GROUPED_PRODUCT_HANDLER_READY:
        'module:grouped_product_handler_ready',
      MODULE_IMAGE_EXPORT_MANAGER_CLEANUP:
        'module:image_export_manager_cleanup',
      MODULE_IMAGE_EXPORT_MANAGER_READY: 'module:image_export_manager_ready',
      MODULE_STATE_INITIALIZER_CLEANUP: 'module:state_initializer_cleanup',
      MODULE_STATE_INITIALIZER_READY: 'module:state_initializer_ready',
      MODULE_SORTING_MANAGER_CLEANUP: 'module:sorting_manager_cleanup',
      MODULE_SORTING_MANAGER_READY: 'module:sorting_manager_ready',
      MODULE_PERSISTENCE_MANAGER_CLEANUP: 'module:persistence_manager_cleanup',
      MODULE_PERSISTENCE_MANAGER_READY: 'module:persistence_manager_ready',
      MODULE_VALIDATION_MANAGER_CLEANUP: 'module:validation_manager_cleanup',
      MODULE_VALIDATION_MANAGER_READY: 'module:validation_manager_ready',
      MODULE_MATERIAL_PREFERENCE_COORDINATOR_CLEANUP:
        'module:material_preference_coordinator_cleanup',
      MODULE_MATERIAL_PREFERENCE_COORDINATOR_READY:
        'module:material_preference_coordinator_ready',
      MODULE_SPACE_CALCULATOR_CLEANUP: 'module:space_calculator_cleanup',
      MODULE_SPACE_CALCULATOR_READY: 'module:space_calculator_ready',
      MODULE_STATE_MANAGER_CLEANUP: 'module:state_manager_cleanup',
      MODULE_STATE_MANAGER_READY: 'module:state_manager_ready',

      // =========================================================================
      // PERFORMANCE EVENTS (performance:*) - 42 events
      // Performance monitoring, metrics, and slow operation detection
      // =========================================================================
      PERFORMANCE_ANIMATION_METRICS: 'performance:animation_metrics',
      PERFORMANCE_AUTO_APPLY_SLOW: 'performance:auto_apply_slow',
      PERFORMANCE_AUTO_NAVIGATION_SLOW: 'performance:auto_navigation_slow',
      PERFORMANCE_BROWSING_COORDINATION_SLOW:
        'performance:browsing_coordination_slow',
      PERFORMANCE_CART_ADD_SLOW: 'performance:cart_add_slow',
      PERFORMANCE_CART_ATTRIBUTES_UPDATE_SLOW:
        'performance:cart_attributes_update_slow',
      PERFORMANCE_CART_BACKGROUND_PREP_SLOW:
        'performance:cart_background_prep_slow',
      PERFORMANCE_CART_PREPARATION_SLOW: 'performance:cart_preparation_slow',
      PERFORMANCE_CART_PRICE_CALC_SLOW: 'performance:cart_price_calc_slow',
      PERFORMANCE_CASCADE_AUTO_SELECTION_SLOW:
        'performance:cascade_auto_selection_slow',
      PERFORMANCE_COMPLETION_CHECK_SLOW: 'performance:completion_check_slow',
      PERFORMANCE_COMPOSITE_PREPROCESSING_SLOW:
        'performance:composite_preprocessing_slow',
      PERFORMANCE_CONFIG_UPDATED: 'performance:config_updated',
      PERFORMANCE_DOM_OPERATION_SLOW: 'performance:dom_operation_slow',
      PERFORMANCE_EVENT_DELEGATION_SETUP: 'performance:event_delegation_setup',
      PERFORMANCE_EVENT_HANDLER_SLOW: 'performance:event_handler_slow',
      PERFORMANCE_EVENT_LISTENER_SETUP: 'performance:event_listener_setup',
      PERFORMANCE_EVENT_SETUP: 'performance:event_setup',
      PERFORMANCE_EVENT_SLOW: 'performance:event_slow',
      PERFORMANCE_FORBIDDEN_VALIDATION_SLOW:
        'performance:forbidden_validation_slow',
      PERFORMANCE_GROUPED_PRODUCT_SELECTION_SLOW:
        'performance:grouped_product_selection_slow',
      PERFORMANCE_HIGH_FREQUENCY: 'performance:high_frequency',
      PERFORMANCE_IMAGE_EXPORT_SLOW: 'performance:image_export_slow',
      PERFORMANCE_IMAGE_LAYER_OPERATION_SLOW:
        'performance:image_layer_operation_slow',
      PERFORMANCE_INITIALIZATION_METRICS: 'performance:initialization_metrics',
      PERFORMANCE_INITIALIZATION_SLOW: 'performance:initialization_slow',
      PERFORMANCE_MATERIAL_PREFERENCE_SLOW:
        'performance:material_preference_slow',
      PERFORMANCE_MATERIAL_SELECTION_SLOW:
        'performance:material_selection_slow',
      PERFORMANCE_MEMORY_SPIKE: 'performance:memory_spike',
      PERFORMANCE_METHOD_EXECUTION: 'performance:method_execution',
      PERFORMANCE_METHOD_SLOW: 'performance:method_slow',
      PERFORMANCE_METRICS_UPDATED: 'performance:metrics_updated',
      PERFORMANCE_NAVIGATION_STRUCTURE_SLOW:
        'performance:navigation_structure_slow',
      PERFORMANCE_PERSISTENCE_OPERATION_SLOW:
        'performance:persistence_operation_slow',
      PERFORMANCE_NAVIGATION_TRANSITION_SLOW:
        'performance:navigation_transition_slow',
      PERFORMANCE_OPERATION_SLOW: 'performance:operation_slow',
      PERFORMANCE_PRICE_CALCULATION_SLOW: 'performance:price_calculation_slow',
      PERFORMANCE_RENDERER3D_SLOW_OPERATION:
        'performance:renderer3d_slow_operation',
      PERFORMANCE_SORTING_CATEGORY_SLOW: 'performance:sorting_category_slow',
      PERFORMANCE_SORTING_VARIANT_SLOW: 'performance:sorting_variant_slow',
      PERFORMANCE_SLOW_OPERATION: 'performance:slow_operation',
      PERFORMANCE_SLOW_UI_OPERATION: 'performance:slow_ui_operation',
      PERFORMANCE_SPACE_CALCULATION_SLOW: 'performance:space_calculation_slow',
      PERFORMANCE_SWIPE_DETECTION_SLOW: 'performance:swipe_detection_slow',
      PERFORMANCE_STATE_CHANGE: 'performance:state_change',
      PERFORMANCE_STATE_INITIALIZATION_SLOW:
        'performance:state_initialization_slow',
      PERFORMANCE_STRUCTURE_INIT_SLOW: 'performance:structure_init_slow',
      PERFORMANCE_THUMBNAIL_GENERATION_SLOW:
        'performance:thumbnail_generation_slow',
      PERFORMANCE_TOOLTIP_CACHE_SLOW: 'performance:tooltip_cache_slow',
      PERFORMANCE_TOOLTIP_DISPLAY_SLOW: 'performance:tooltip_display_slow',
      PERFORMANCE_URL_PARAM_PARSE_SLOW: 'performance:url_param_parse_slow',
      PERFORMANCE_VALIDATION_SLOW: 'performance:validation_slow',

      // =========================================================================
      // PERSISTENCE EVENTS (persistence:*) - 10 events
      // Save, share, and clear persistence operations with 5-stage lifecycle
      // =========================================================================
      PERSISTENCE_CLEAR_CONFIRMED: 'persistence:clear_confirmed',
      PERSISTENCE_CLEAR_REQUESTED: 'persistence:clear_requested',
      PERSISTENCE_RECOVERY_FAILED: 'persistence:recovery_failed',
      PERSISTENCE_RECOVERY_PROGRESS: 'persistence:recovery_progress',
      PERSISTENCE_RECOVERY_STARTED: 'persistence:recovery_started',
      PERSISTENCE_RECOVERY_SUCCESS: 'persistence:recovery_success',
      PERSISTENCE_RECOVERY_VALIDATION: 'persistence:recovery_validation',
      PERSISTENCE_SAVE_REQUESTED: 'persistence:save_requested',
      PERSISTENCE_SHARE_REQUESTED: 'persistence:share_requested',
      PERSISTENCE_STORAGE_CLEARED: 'persistence:storage_cleared',

      // =========================================================================
      // PPD EVENTS (ppd:*) - 1 event
      // Perfect Product Dictionary updates
      // =========================================================================
      PPD_COMPOSITE_SCORES_UPDATED: 'ppd:composite_scores_updated',

      // =========================================================================
      // PRICE EVENTS (price:*) - 4 events
      // Price calculation requests and responses
      // =========================================================================
      PRICE_CALCULATION_COMPLETED: 'price:calculation_completed',
      PRICE_CALCULATION_REQUESTED: 'price:calculation_requested',
      PRICE_CALCULATION_RESPONSE: 'price:calculation_response',
      PRICE_REQUEST_TOTAL_CALCULATION: 'price:request_total_calculation',

      // =========================================================================
      // RECOVERY EVENTS (recovery:*) - 4 events
      // State recovery and animation restoration
      // =========================================================================
      RECOVERY_ANIMATE_SELECTIONS: 'recovery:animate_selections',
      RECOVERY_ANIMATION_COMPLETE: 'recovery:animation_complete',
      RECOVERY_ANIMATION_ERROR: 'recovery:animation_error',
      RECOVERY_SELECTION_ANIMATED: 'recovery:selection_animated',

      // =========================================================================
      // RESTORATION EVENTS (restoration:*) - 1 event
      // Configuration restoration completion
      // =========================================================================
      RESTORATION_COMPLETE: 'restoration:complete',

      // =========================================================================
      // SELECTION EVENTS (selection:*) - 7 events
      // User selection state and browsing events
      // =========================================================================
      SELECTION_APPLIED: 'selection:applied',
      SELECTION_AUTO_APPLY_COMPLETED: 'selection:auto_apply_completed',
      SELECTION_AUTO_APPLY_REQUEST: 'selection:auto_apply_request',
      SELECTION_AUTO_APPLY_SKIPPED: 'selection:auto_apply_skipped',
      SELECTION_BROWSING: 'selection:browsing',
      SELECTION_USER_UNAPPLY_COMPLETED: 'selection:user_unapply_completed',
      SELECTION_USER_UNAPPLY_FAILED: 'selection:user_unapply_failed',
      SELECTION_USER_UNAPPLY_REQUESTED: 'selection:user_unapply_requested',
      // apo_style ONLY. Published instead of SELECTION_USER_UNAPPLY_REQUESTED
      // when the card being reset is the material master (Wood & Finish), so
      // md-config-bl-material-remap-coordinator.js can warn BEFORE anything is
      // cleared. It answers with the unapply itself. Every other category still
      // publishes the unapply directly -- this keeps the blast radius on wood.
      SELECTION_VARIANT_APPLIED: 'selection:variant_applied',

      // =========================================================================
      // SUPPORT EVENTS (support:*) - 41 events
      // Support module lifecycle events for progress tracking, validation, and sorting
      // =========================================================================
      SUPPORT_SORTING_CATEGORY_COMPLETED: 'support:sorting_category_completed',
      SUPPORT_SORTING_CATEGORY_FAILED: 'support:sorting_category_failed',
      SUPPORT_SORTING_CATEGORY_PROGRESS: 'support:sorting_category_progress',
      SUPPORT_SORTING_CATEGORY_STARTED: 'support:sorting_category_started',
      SUPPORT_SORTING_CATEGORY_VALIDATION:
        'support:sorting_category_validation',
      SUPPORT_SORTING_VARIANT_COMPLETED: 'support:sorting_variant_completed',
      SUPPORT_SORTING_VARIANT_FAILED: 'support:sorting_variant_failed',
      SUPPORT_SORTING_VARIANT_STARTED: 'support:sorting_variant_started',
      SUPPORT_SORTING_CACHE_CLEARED: 'support:sorting_cache_cleared',
      SUPPORT_PROGRESS_CACHE_INVALIDATED: 'support:progress_cache_invalidated',
      SUPPORT_PROGRESS_CALCULATION_COMPLETED:
        'support:progress_calculation_completed',
      SUPPORT_PROGRESS_CALCULATION_FAILED:
        'support:progress_calculation_failed',
      SUPPORT_PROGRESS_CALCULATION_PROGRESS:
        'support:progress_calculation_progress',
      SUPPORT_PROGRESS_CALCULATION_STARTED:
        'support:progress_calculation_started',
      SUPPORT_PROGRESS_CALCULATION_VALIDATION:
        'support:progress_calculation_validation',
      SUPPORT_PROGRESS_CLEANUP: 'support:progress_cleanup',
      SUPPORT_PROGRESS_INITIALIZED: 'support:progress_initialized',
      SUPPORT_PROGRESS_INITIALIZATION_FAILED:
        'support:progress_initialization_failed',
      SUPPORT_PROGRESS_READY: 'support:progress_ready',
      SUPPORT_VALIDATION_INIT_COMPLETED: 'support:validation_init_completed',
      SUPPORT_VALIDATION_INIT_FAILED: 'support:validation_init_failed',
      SUPPORT_VALIDATION_INIT_STARTED: 'support:validation_init_started',
      SUPPORT_VALIDATION_INIT_VALIDATION: 'support:validation_init_validation',
      SUPPORT_VALIDATION_STATE_CHANGE_COMPLETED:
        'support:validation_state_change_completed',
      SUPPORT_VALIDATION_STATE_CHANGE_FAILED:
        'support:validation_state_change_failed',
      SUPPORT_VALIDATION_STATE_CHANGE_PROGRESS:
        'support:validation_state_change_progress',
      SUPPORT_VALIDATION_STATE_CHANGE_STARTED:
        'support:validation_state_change_started',
      SUPPORT_VALIDATION_STATE_CHANGE_VALIDATION:
        'support:validation_state_change_validation',
      SUPPORT_URL_RECOVERY_INIT_STARTED: 'support:url_recovery_init_started',
      SUPPORT_URL_RECOVERY_INIT_COMPLETED:
        'support:url_recovery_init_completed',
      SUPPORT_URL_RECOVERY_INIT_FAILED: 'support:url_recovery_init_failed',
      SUPPORT_URL_RECOVERY_ANIMATION_STARTED:
        'support:url_recovery_animation_started',
      SUPPORT_URL_RECOVERY_ANIMATION_VALIDATION:
        'support:url_recovery_animation_validation',
      SUPPORT_URL_RECOVERY_ANIMATION_PROGRESS:
        'support:url_recovery_animation_progress',
      SUPPORT_URL_RECOVERY_ANIMATION_COMPLETED:
        'support:url_recovery_animation_completed',
      SUPPORT_URL_RECOVERY_ANIMATION_FAILED:
        'support:url_recovery_animation_failed',
      SUPPORT_URL_RECOVERY_CLEANUP: 'support:url_recovery_cleanup',
      SUPPORT_URL_RECOVERY_READY: 'support:url_recovery_ready',

      // =========================================================================
      // STATE EVENTS (state:*) - 35 events
      // State management, transitions, recovery, and conflict resolution
      // =========================================================================
      STATE_APPLIED_CONFLICT_DETECTED: 'state:applied_conflict_detected',
      STATE_BROWSING_RESULT: 'state:browsing_result',
      STATE_CATEGORY_COMPLETED: 'state:category_completed',
      STATE_CATEGORY_INCOMPLETE: 'state:category_incomplete', // Published in state-manager ternary (no external subscribers)
      STATE_CATEGORY_TOGGLED: 'state:category_toggled',
      STATE_CATEGORY_TYPE_STORED: 'state:category_type_stored',
      STATE_CHANGE_COORDINATED: 'state:change_coordinated',
      STATE_CHANGE_ERROR: 'state:change_error',
      STATE_CHANGE_REQUESTED: 'state:change_requested',
      STATE_CHANGED: 'state:changed',
      STATE_FORBIDDEN_STATE_RECORDED: 'state:forbidden_state_recorded',
      STATE_MATERIAL_PREFERENCES_CLEARED: 'state:material_preferences_cleared',
      STATE_MATERIAL_PREFERENCES_UPDATED: 'state:material_preferences_updated',
      STATE_MATERIAL_STATE_REQUESTED: 'state:material_state_requested',
      STATE_MATERIAL_STATE_RESET_REQUESTED:
        'state:material_state_reset_requested',
      STATE_MATERIAL_STATE_RESPONSE: 'state:material_state_response',
      STATE_PROGRESS_UPDATED: 'state:progress_updated',
      STATE_RECOVERY_FAILED: 'state:recovery_failed',
      STATE_RECOVERY_INITIALIZATION_FAILED:
        'state:recovery_initialization_failed',
      STATE_RECOVERY_INITIALIZATION_STARTED:
        'state:recovery_initialization_started',
      STATE_RECOVERY_INITIALIZED: 'state:recovery_initialized',
      STATE_RECOVERY_REQUESTED: 'state:recovery_requested',
      STATE_RECOVERY_SUCCEEDED: 'state:recovery_succeeded',
      STATE_REQUEST_CART_CONFIGURATION: 'state:request_cart_configuration',
      STATE_SELECTION_CLEARED: 'state:selection_cleared',
      STATE_SELECTION_REMOVED: 'state:selection_removed',
      STATE_SELECTIONS_RESPONSE: 'state:selections_response',
      STATE_TAB_CHANGED: 'state:tab_changed',
      STATE_TAB_COMPLETED: 'state:tab_completed',
      STATE_TAB_INCOMPLETE: 'state:tab_incomplete', // Published in state-manager ternary (no external subscribers)
      STATE_TRANSITION_STARTED: 'state:transition_started',
      STATE_UNKNOWN_ACTION: 'state:unknown_action',
      STATE_UPDATE_FORBIDDEN_STATES: 'state:update_forbidden_states',
      STATE_VALIDATION_ERROR: 'state:validation_error',

      // =========================================================================
      // SYSTEM EVENTS (system:*) - 61 events
      // Core system lifecycle, initialization, and infrastructure
      // =========================================================================
      SYSTEM_BOOTSTRAP_COMPLETE: 'system:bootstrap_complete',
      SYSTEM_CATEGORY_TYPE_ANALYZED: 'system:category_type_analyzed',
      SYSTEM_CHANGED: 'system:changed',
      SYSTEM_CONFIGURATION_LOADED: 'system:configuration_loaded',
      SYSTEM_CONFIGURATION_LOADING_STARTED:
        'system:configuration_loading_started',
      SYSTEM_CONSISTENCY_CHECK_REQUESTED: 'system:consistency_check_requested',
      SYSTEM_CONSISTENCY_CHECK_RESPONSE: 'system:consistency_check_response',
      SYSTEM_CORE_INITIALIZATION_STARTED: 'system:core_initialization_started',
      SYSTEM_DEPENDENCIES_VALIDATED: 'system:dependencies_validated',
      SYSTEM_DOM_ERROR: 'system:dom_error',
      SYSTEM_DOM_READY: 'system:dom_ready', // Published by event-bus internals (no external subscribers)
      SYSTEM_ERROR: 'system:error',
      SYSTEM_EVENT_BUS_INITIALIZED: 'system:event_bus_initialized', // Published by event-bus internals (no external subscribers)
      SYSTEM_EVENT_LISTENER_SETUP_FAILED: 'system:event_listener_setup_failed',
      SYSTEM_EVENT_LISTENER_SETUP_STARTED:
        'system:event_listener_setup_started',
      SYSTEM_EVENT_LISTENERS_INITIALIZED: 'system:event_listeners_initialized',
      SYSTEM_HISTORY_CLEARED: 'system:history_cleared', // Published by event-bus internals (no external subscribers)
      SYSTEM_INITIALIZATION_COMPLETE: 'system:initialization_complete',
      SYSTEM_INITIALIZATION_FAILED: 'system:initialization_failed',
      SYSTEM_INITIALIZATION_RESET: 'system:initialization_reset',
      SYSTEM_INITIALIZATION_STARTED: 'system:initialization_started',
      SYSTEM_INITIALIZED: 'system:initialized',
      SYSTEM_JQUERY_MISSING: 'system:jquery_missing', // Published by event-bus internals (no external subscribers)
      SYSTEM_MAPS_INITIALIZED: 'system:maps_initialized',
      SYSTEM_MATERIAL_RELATIONSHIPS_BUILT:
        'system:material_relationships_built',
      SYSTEM_MODULE_READY: 'system:module_ready',
      SYSTEM_PERSISTENCE_OPERATION_COMPLETED:
        'system:persistence_operation_completed',
      SYSTEM_PERSISTENCE_OPERATION_FAILED:
        'system:persistence_operation_failed',
      SYSTEM_PERSISTENCE_OPERATION_STARTED:
        'system:persistence_operation_started',
      SYSTEM_PIPELINE_COMPLETE: 'system:pipeline_complete',
      SYSTEM_PRE_STRUCTURE_INITIALIZATION:
        'system:pre_structure_initialization',
      SYSTEM_PROGRESS_UPDATED: 'system:progress_updated',
      SYSTEM_READY: 'system:ready',
      SYSTEM_STATE_CHANGE: 'system:state_change',
      SYSTEM_STATE_CLEARED: 'system:state_cleared',
      SYSTEM_STATE_FROM_PPD_READY: 'system:state_from_ppd_ready',
      SYSTEM_STATE_INITIALIZATION_FAILED: 'system:state_initialization_failed',
      SYSTEM_STATE_INITIALIZATION_PROGRESS:
        'system:state_initialization_progress',
      SYSTEM_STATE_INITIALIZATION_STARTED:
        'system:state_initialization_started',
      SYSTEM_STATE_INITIALIZATION_VALIDATION:
        'system:state_initialization_validation',
      SYSTEM_STATE_INITIALIZED: 'system:state_initialized',
      SYSTEM_STATE_RECOVERY_INITIATED: 'system:state_recovery_initiated',
      SYSTEM_STATE_RECOVERY_REQUESTED: 'system:state_recovery_requested',
      SYSTEM_STATE_RECOVERY_RESPONSE: 'system:state_recovery_response',
      SYSTEM_SHUTDOWN: 'system:shutdown',
      SYSTEM_STRUCTURE_INITIALIZATION_COMPLETED:
        'system:structure_initialization_completed',
      SYSTEM_STRUCTURE_INITIALIZATION_FAILED:
        'system:structure_initialization_failed',
      SYSTEM_STRUCTURE_INITIALIZATION_STARTED:
        'system:structure_initialization_started',
      SYSTEM_SYNC_TIMER_TICK: 'system:sync_timer_tick',
      SYSTEM_TAB_CHANGED: 'system:tab_changed',
      SYSTEM_UI_QUEUE_PROCESSED: 'system:ui_queue_processed', // Published by event-bus internals (no external subscribers)
      SYSTEM_URL_PARAM_DETECTED: 'system:url_param_detected',
      SYSTEM_URL_PARAM_PARSE_FAILED: 'system:url_param_parse_failed',
      SYSTEM_URL_PARAM_PARSE_START: 'system:url_param_parse_start',
      SYSTEM_URL_PARAM_PARSE_SUCCESS: 'system:url_param_parse_success',
      SYSTEM_URL_PARAM_VALIDATION_FAILED: 'system:url_param_validation_failed',
      SYSTEM_URL_PARAM_VALIDATION_START: 'system:url_param_validation_start',
      SYSTEM_URL_PARAM_VALIDATION_SUCCESS:
        'system:url_param_validation_success',
      SYSTEM_VALIDATION_FAILED: 'system:validation_failed',

      // =========================================================================
      // UI EVENTS (ui:*) - 154 events
      // UI updates, toggles, navigation, thumbnails, confetti, tooltips, DOM ops
      // =========================================================================
      UI_ADD_RECOVERY_COMPLETE_EFFECT: 'ui:add_recovery_complete_effect',
      UI_ADD_TO_CART_ALLOWED: 'ui:add_to_cart_allowed',
      UI_ADD_TO_CART_DEBOUNCED: 'ui:add_to_cart_debounced',
      UI_ADD_TO_CART_DISABLED_CLICKED: 'ui:add_to_cart_disabled_clicked',
      // Fired once the rail's Add to Cart block has been MOVED into the sticky
      // actions bar on 100% completion (apo_style only -- see
      // md-config-ui-updater.js _promoteAddToCartToStickyBar). Diagnostic:
      // a celebration that bursts from the wrong place can be traced to
      // whether this ever fired, rather than by re-deriving the DOM.
      UI_ADD_TO_CART_PROMOTED: 'ui:add_to_cart_promoted',
      // Ryan, UAT 2026-08-27: a disabled Add to Cart scrolls the customer to
      // the category still missing an answer "with it highlighted in the
      // warning border color". The scroll is auto-navigation's job; this is
      // how the BL coordinator asks the UI layer for the highlight without
      // touching the DOM itself (CCP rule 4).
      UI_PENDING_CATEGORY_CALLOUT: 'ui:pending_category_callout',
      UI_ANIMATION_STATE_RESULT: 'ui:animation_state_result',
      UI_APPEND_ERROR_ELEMENT: 'ui:append_error_element',
      UI_APPLIED_LAYER_REMOVE: 'ui:applied_layer_remove',
      UI_APPLIED_LAYER_UPDATE: 'ui:applied_layer_update',
      UI_APPLY_BUTTON_CLICKED: 'ui:apply_button_clicked',
      UI_APPLY_SELECTION_COMPLETED: 'ui:apply_selection_completed',
      UI_APPLY_SELECTION_DELEGATING: 'ui:apply_selection_delegating',
      UI_APPLY_SELECTION_FAILED: 'ui:apply_selection_failed',
      UI_APPLY_SELECTION_STATE_RETRIEVAL_STARTED:
        'ui:apply_selection_state_retrieval_started',
      UI_APPLY_SELECTION_STATE_RETRIEVED: 'ui:apply_selection_state_retrieved',
      UI_APPLY_SELECTION_VALIDATION_PASSED:
        'ui:apply_selection_validation_passed',
      UI_AUTO_TOGGLE_VIEW: 'ui:auto_toggle_view',
      UI_BACK_BUTTON_CLICKED: 'ui:back_button_clicked',
      UI_BACK_NAVIGATION_COMPLETED: 'ui:back_navigation_completed',
      UI_BLUEPRINT_CALLOUTS_RENDERED: 'ui:blueprint_callouts_rendered',
      UI_BLUEPRINT_MODE_CHANGED: 'ui:blueprint_mode_changed',
      // Request event, not a command: a peer UI module publishes this to ASK
      // blueprint-overlay to toggle its own mode; blueprint-overlay subscribes
      // internally and calls its own private setActive() in response, so the
      // ghost-click debounce and epoch guard stay owned by the one module
      // whose state they protect. A direct cross-module call into that
      // mutating method from elsewhere was reviewed and rejected — see
      // CLAUDE.md's "Traveling Preview" entry for the reasoning.
      // Optional payload `active: true|false` asks for a SPECIFIC mode rather
      // than a flip (no-op when already there); omitted = toggle, as before.
      UI_BLUEPRINT_MODE_TOGGLE_REQUESTED: 'ui:blueprint_mode_toggle_requested',
      UI_BLUEPRINT_OVERLAY_READY: 'ui:blueprint_overlay_ready',
      UI_BROWSING_LAYER_REMOVE: 'ui:browsing_layer_remove',
      UI_BROWSING_LAYER_SHOW: 'ui:browsing_layer_show',
      UI_BUTTON_TOOLTIP_REGISTRATION_FAILED:
        'ui:button_tooltip_registration_failed',
      UI_BUTTON_TOOLTIP_REGISTRATION_START:
        'ui:button_tooltip_registration_start',
      UI_BUTTON_TOOLTIP_REGISTRATION_SUCCESS:
        'ui:button_tooltip_registration_success',
      UI_CART_ADD_COMPLETED: 'ui:cart_add_completed',
      UI_CART_ADD_FAILED: 'ui:cart_add_failed',
      UI_CART_ADDING_OPTIMISTIC: 'ui:cart_adding_optimistic',
      UI_CART_BUTTON_CLICKED: 'ui:cart_button_clicked',
      UI_CART_ERROR_MODAL_SHOW_START: 'ui:cart_error_modal_show_start',
      UI_CART_ERROR_MODAL_SHOW_SUCCESS: 'ui:cart_error_modal_show_success',
      UI_CART_ERROR_MODAL_SHOW_FAILED: 'ui:cart_error_modal_show_failed',
      UI_CART_ERROR_MODAL_DISMISSED: 'ui:cart_error_modal_dismissed',
      UI_CART_ERROR_MODAL_RETRY: 'ui:cart_error_modal_retry',
      UI_CART_ERROR_MODAL_COPY_SUCCESS: 'ui:cart_error_modal_copy_success',
      UI_CART_ERROR_MODAL_COPY_FAILED: 'ui:cart_error_modal_copy_failed',
      UI_CART_ERROR_MODAL_READY: 'ui:cart_error_modal_ready',
      UI_CART_ERROR_MODAL_CLEANUP: 'ui:cart_error_modal_cleanup',
      UI_CATEGORIES_SHOWN_FOR_TAB: 'ui:categories_shown_for_tab',
      UI_CATEGORY_AND_VARIANTS_NEED_RERENDER:
        'ui:category_and_variants_need_rerender',
      UI_CATEGORY_CLICKED: 'ui:category_clicked',
      UI_CLOSE_CATEGORY_ICON_CLICKED: 'ui:close_category_icon_clicked',
      UI_CATEGORY_COLLAPSE_FAILED: 'ui:category_collapse_failed',
      UI_CATEGORY_COLLAPSED: 'ui:category_collapsed',
      UI_CATEGORY_CREATED: 'ui:category_created',
      UI_CATEGORY_EXPANDED: 'ui:category_expanded',
      UI_CATEGORY_EXPANSION_FAILED: 'ui:category_expansion_failed',
      UI_CATEGORY_EXPANSION_STARTED: 'ui:category_expansion_started',
      UI_CATEGORY_OPEN_REQUEST: 'ui:category_open_request',
      UI_CATEGORY_CONTENT_RENDERED: 'ui:category_content_rendered',
      // Category header pictograms (md-config-ui-category-pictograms.js).
      // RENDERED carries a `stage` (start/validation/progress/success/failed),
      // the same single-event lifecycle UI_SLIDER_RENDERED uses.
      UI_CATEGORY_PICTOGRAMS_READY: 'ui:category_pictograms_ready',
      UI_CATEGORY_PICTOGRAM_RENDERED: 'ui:category_pictogram_rendered',
      UI_CATEGORY_PICTOGRAMS_CLEANUP: 'ui:category_pictograms_cleanup',
      UI_CATEGORY_OPENED: 'ui:category_opened',
      UI_CATEGORY_TOGGLE_COMPLETED: 'ui:category_toggle_completed',
      UI_CHECK_ANIMATION_STATE: 'ui:check_animation_state',
      UI_CHECK_ELEMENT_CLASS: 'ui:check_element_class',
      UI_COMPOSITE_PRODUCT_SELECTION_FAILED:
        'ui:composite_product_selection_failed',
      UI_COMPOSITE_THUMBNAIL_GENERATED: 'ui:composite_thumbnail_generated',
      UI_COMPOSITE_THUMBNAIL_GENERATION_FAILED:
        'ui:composite_thumbnail_generation_failed',
      UI_COMPOSITE_THUMBNAIL_GENERATION_REQUESTED:
        'ui:composite_thumbnail_generation_requested',
      UI_CTA_ENGAGED: 'ui:cta_engaged',
      UI_CONFETTI_CANVAS_INITIALIZE: 'ui:confetti_canvas_initialize',
      UI_CONFETTI_CANVAS_INITIALIZED: 'ui:confetti_canvas_initialized',
      UI_CONFETTI_CANVAS_REQUESTED: 'ui:confetti_canvas_requested',
      UI_CONFETTI_CELEBRATION_FAILED: 'ui:confetti_celebration_failed',
      UI_CONFETTI_CELEBRATION_PROGRESS: 'ui:confetti_celebration_progress',
      UI_CONFETTI_CELEBRATION_REQUESTED: 'ui:confetti_celebration_requested',
      UI_CONFETTI_CELEBRATION_START: 'ui:confetti_celebration_start',
      UI_CONFETTI_CELEBRATION_SUCCESS: 'ui:confetti_celebration_success',
      UI_CONFETTI_CELEBRATION_VALIDATION: 'ui:confetti_celebration_validation',
      UI_CONFETTI_CLEANUP: 'ui:confetti_cleanup',
      UI_CONFETTI_COMPLETED: 'ui:confetti_completed',
      UI_CONFETTI_COORDINATES_REQUESTED: 'ui:confetti_coordinates_requested',
      UI_CONFETTI_COORDINATES_RESPONSE: 'ui:confetti_coordinates_response',
      UI_CONFETTI_READY: 'ui:confetti_ready',
      UI_CONFETTI_STARTED: 'ui:confetti_started',
      UI_CONFETTI_VIEWPORT_UPDATED: 'ui:confetti_viewport_updated',
      UI_CONTENT_UPDATED: 'ui:content_updated',

      // Image Export Events
      UI_IMAGE_EXPORT_STARTED: 'ui:image_export_started',
      UI_IMAGE_EXPORT_VALIDATION: 'ui:image_export_validation',
      UI_IMAGE_EXPORT_FRONT_CAPTURE_STARTED:
        'ui:image_export_front_capture_started',
      UI_IMAGE_EXPORT_FRONT_CAPTURE_COMPLETE:
        'ui:image_export_front_capture_complete',
      UI_IMAGE_EXPORT_BACK_CAPTURE_STARTED:
        'ui:image_export_back_capture_started',
      UI_IMAGE_EXPORT_BACK_CAPTURE_COMPLETE:
        'ui:image_export_back_capture_complete',
      UI_IMAGE_EXPORT_DOWNLOAD_START: 'ui:image_export_download_start',
      UI_IMAGE_EXPORT_DOWNLOAD_COMPLETE: 'ui:image_export_download_complete',
      UI_IMAGE_EXPORT_FAILED: 'ui:image_export_failed',
      UI_IMAGE_EXPORT_READY: 'ui:image_export_ready',
      // Read-only render hook (imageExportManager.renderConfigurationImage),
      // used by the Super App render job. Separate from the Save-download
      // lifecycle above so a render never drives the Save UI.
      UI_IMAGE_EXPORT_RENDER_REQUESTED: 'ui:image_export_render_requested',
      UI_IMAGE_EXPORT_RENDER_COMPLETED: 'ui:image_export_render_completed',
      UI_IMAGE_EXPORT_RENDER_FAILED: 'ui:image_export_render_failed',
      // "Build this": the PDP gallery settled on a preset slide (thumbnail,
      // swipe, arrow). Published by md-config-ui-build-preset.js,
      // consumed by the analytics reporter (build_this_slide_view, once per
      // handle per page). Payload { handle, position }.
      UI_BUILD_PRESET_SLIDE_VIEWED: 'ui:build_preset_slide_viewed',
      // "Build this" was tapped / keyed (md-config-ui-build-preset.js, which
      // captures taps from DOMContentLoaded). Consumed by
      // md-config-support-build-preset.js. Payload { handle, origin,
      // canConfirm } (canConfirm: the confirmation dialog can open).
      UI_BUILD_PRESET_ACTIVATED: 'ui:build_preset_activated',
      // The customer answered the confirmation dialog. Payload { requestId,
      // handle, confirmed } (false: "Keep my design", Esc or a close).
      UI_BUILD_PRESET_CONFIRM_RESOLVED: 'ui:build_preset_confirm_resolved',
      // Re-ring the applied tile in each listed category card, clearing the
      // stale ring a bulk apply (which never passes through browsing) leaves
      // behind. Published by md-config-support-build-preset.js after a preset
      // settles; consumed by md-config-ui-updater.js. Payload { categoryIds }.
      UI_APPLIED_TILE_RINGS_SYNC_REQUESTED:
        'ui:applied_tile_rings_sync_requested',
      UI_CREATE_ELEMENT: 'ui:create_element',
      UI_DELIVERY_DISPLAY_UPDATED: 'ui:delivery_display_updated', // TODO: Wire up when UI delivery display is published post-update
      UI_RUSH_DELIVERY_DESELECT_REQUESTED:
        'ui:rush_delivery_deselect_requested', // Published by UI updater when rush fee must be auto-deselected (hidden boundary crossed)
      UI_DOM_WRAPPER_ERROR: 'ui:dom_wrapper_error',
      UI_DOM_WRAPPER_REQUESTED: 'ui:dom_wrapper_requested',
      UI_DOM_WRAPPER_RESPONSE: 'ui:dom_wrapper_response',
      UI_ELEMENT_SEARCH_RESULT: 'ui:element_search_result',
      UI_EVENT_DELEGATION_INITIALIZED: 'ui:event_delegation_initialized',
      UI_EVENT_DELEGATION_STARTED: 'ui:event_delegation_started',
      UI_EVENT_HANDLER_INITIALIZATION_DEFERRED:
        'ui:event_handler_initialization_deferred',
      UI_EVENT_HANDLER_INITIALIZED: 'ui:event_handler_initialized',
      UI_EVENT_LISTENERS_INITIALIZED: 'ui:event_listeners_initialized',
      UI_EVENT_SETUP_STARTED: 'ui:event_setup_started',
      UI_FORBIDDEN_TOOLTIP_REGISTRATION_FAILED:
        'ui:forbidden_tooltip_registration_failed',
      UI_FORBIDDEN_TOOLTIP_REGISTRATION_START:
        'ui:forbidden_tooltip_registration_start',
      UI_FORBIDDEN_TOOLTIP_REGISTRATION_SUCCESS:
        'ui:forbidden_tooltip_registration_success',
      UI_HIDE_LOADING_SPINNER: 'ui:hide_loading_spinner',
      UI_HIDE_MODAL: 'ui:hide_modal',
      UI_HIGHLIGHT_ELEMENT: 'ui:highlight_element',
      UI_IMPLICIT_APPLY_NAVIGATE_REQUEST: 'ui:implicit_apply_navigate_request',
      UI_INITIALIZE_TOOLTIPS: 'ui:initialize_tooltips',
      UI_INJECT_STYLES: 'ui:inject_styles',
      UI_LOADING_STATE_CHANGED: 'ui:loading_state_changed',
      // Mobile live metrics dashboard under the Width/Height/Door Depth sliders
      // (md-config-ui-metrics-dashboard.js). RENDERED carries a `stage`
      // (start/validation/progress/success/failed), the same single-event
      // lifecycle UI_SLIDER_RENDERED uses.
      UI_METRICS_DASHBOARD_READY: 'ui:metrics_dashboard_ready',
      UI_METRICS_DASHBOARD_RENDERED: 'ui:metrics_dashboard_rendered',
      UI_METRICS_DASHBOARD_CLEANUP: 'ui:metrics_dashboard_cleanup',
      UI_MULTI_VARIANT_DISPLAY_FAILED: 'ui:multi_variant_display_failed',
      // Mobile Traveling Preview sheet (collapsed chip <-> expanded bottom
      // sheet). Firm STATE_CHANGED for the open/close toggle itself (modeled
      // on UI_TOOLTIP_STATE_CHANGED), plus a STARTED/SUCCESS/FAILED triple for
      // the expanded-view image load specifically, since that fetch is
      // fallible/async and the simple toggle is not.
      UI_MOBILE_PREVIEW_SHEET_STATE_CHANGED: 'ui:mobile_preview_sheet_state_changed',
      // Request event (same contract as UI_BLUEPRINT_MODE_TOGGLE_REQUESTED): a
      // peer UI module ASKS the Traveling Preview sheet to open; the sheet
      // module decides, on its own guards (<=800px, sheet present, not already
      // open). Published by md-config-ui-metrics-dashboard.js "See it on the plan".
      UI_MOBILE_PREVIEW_SHEET_OPEN_REQUESTED:
        'ui:mobile_preview_sheet_open_requested',
      UI_MOBILE_PREVIEW_IMAGE_LOAD_STARTED: 'ui:mobile_preview_image_load_started',
      UI_MOBILE_PREVIEW_IMAGE_LOAD_SUCCESS: 'ui:mobile_preview_image_load_success',
      UI_MOBILE_PREVIEW_IMAGE_LOAD_FAILED: 'ui:mobile_preview_image_load_failed',
      UI_NAVIGATION_STRUCTURE_CREATION_COMPLETED:
        'ui:navigation_structure_creation_completed',
      UI_NAVIGATION_STRUCTURE_CREATION_FAILED:
        'ui:navigation_structure_creation_failed',
      UI_NAVIGATION_STRUCTURE_CREATION_STARTED:
        'ui:navigation_structure_creation_started',
      UI_PLACEHOLDER_RERENDERED: 'ui:placeholder_rerendered',
      UI_PRODUCT_GROUP_CLICKED: 'ui:product_group_clicked',
      UI_PROGRESS_BAR_COMPLETED: 'ui:progress_bar_completed',
      UI_PROGRESS_BAR_UPDATE_FAILED: 'ui:progress_bar_update_failed',
      UI_PASS_THROUGH_TOOLTIP_REGISTRATION_FAILED:
        'ui:pass_through_tooltip_registration_failed',
      UI_PASS_THROUGH_TOOLTIP_REGISTRATION_START:
        'ui:pass_through_tooltip_registration_start',
      UI_PASS_THROUGH_TOOLTIP_REGISTRATION_SUCCESS:
        'ui:pass_through_tooltip_registration_success',
      UI_PROGRESS_BAR_UPDATE_STARTED: 'ui:progress_bar_update_started',
      UI_PROGRESS_CALCULATION_REQUESTED: 'ui:progress_calculation_requested',
      UI_PROGRESS_CELEBRATION_TRIGGERED: 'ui:progress_celebration_triggered',
      UI_PROGRESS_PERCENTAGE_UPDATED: 'ui:progress_percentage_updated',
      UI_PROGRESS_UPDATE_REQUESTED: 'ui:progress_update_requested',
      UI_REGISTER_CUSTOM_TOOLTIP: 'ui:register_custom_tooltip',
      UI_REMOVE_ELEMENT: 'ui:remove_element',
      UI_RENDERER3D_CLEANUP_COMPLETED: 'ui:renderer3d_cleanup_completed',
      UI_RENDERER3D_GEOMETRY_SWAPPED: 'ui:renderer3d_geometry_swapped',
      UI_RENDERER3D_INITIALIZATION_COMPLETED:
        'ui:renderer3d_initialization_completed',
      UI_RENDERER3D_INITIALIZATION_FAILED:
        'ui:renderer3d_initialization_failed',
      UI_RENDERER3D_INITIALIZATION_PROGRESS:
        'ui:renderer3d_initialization_progress',
      UI_RENDERER3D_INITIALIZATION_STARTED:
        'ui:renderer3d_initialization_started',
      UI_RENDERER3D_INITIALIZED: 'ui:renderer3d_initialized',
      UI_RENDERER3D_MATERIAL_APPLIED: 'ui:renderer3d_material_applied',
      UI_RENDERER3D_OPERATION_COMPLETED: 'ui:renderer3d_operation_completed',
      UI_RENDERER3D_OPERATION_FAILED: 'ui:renderer3d_operation_failed',
      UI_RENDERER3D_OPERATION_PROGRESS: 'ui:renderer3d_operation_progress',
      UI_RENDERER3D_OPERATION_STARTED: 'ui:renderer3d_operation_started',
      UI_RENDERER3D_OPERATION_VALIDATION: 'ui:renderer3d_operation_validation',
      UI_REQUEST_CART_SECTIONS: 'ui:request_cart_sections',
      UI_SEARCH_ELEMENT: 'ui:search_element',
      UI_SEARCH_ELEMENTS: 'ui:search_elements',
      UI_SELECTION_FAILED: 'ui:selection_failed',
      UI_SELECTION_FORBIDDEN: 'ui:selection_forbidden',
      // A click on an ALREADY-GREYED tile that an early-return guard in
      // md-config-core-event-handler.js refused before any state change.
      // Distinct from UI_SELECTION_FORBIDDEN, which the conductor publishes when
      // a LIVE selection turns out to conflict: by the time a tile is pre-greyed
      // the conductor is never reached, so the refusal produced no bus traffic at
      // all. Measured live 2026-09-18: three clicks on the greyed 6-9/16 jamb,
      // zero events. That dead click is the configurator's best friction signal,
      // so the guards now announce it. Payload:
      // { categoryId, variantId, productId, reason, timestamp }.
      UI_FORBIDDEN_TILE_CLICK_REFUSED: 'ui:forbidden_tile_click_refused',
      // A tap that produced NO visible response and NO other bus traffic — the
      // sibling of UI_FORBIDDEN_TILE_CLICK_REFUSED for surfaces that are not
      // forbidden at all, merely inert. Five were measured live in apo_style +
      // click-apply: the card header (cards never fold there, so the header is
      // a no-op), an already-applied tile re-tap, the step dot for the step you
      // are already on, the price/shipping readouts (the tooltips they used to
      // open were deleted in 9c1c9f84 but they still LOOK tappable), and a
      // slider +/- at its bound.
      //
      // 🚨 PUBLISH-ONLY. A publisher must change NOTHING else about its branch —
      // this event exists to MEASURE the inertness, not to fix it. A fix that
      // ships later removes the publish site along with the inertness.
      //
      // `surface` is a CLOSED enum — CONSTANTS.INERT_TAP_SURFACES — so the
      // reporter's element_label can never carry free text. Payload:
      // { surface, categoryId?, variantId?, direction?, timestamp, source }.
      UI_INERT_TAP_DETECTED: 'ui:inert_tap_detected',
      // ---- "make the inert taps mean something" (2026-09-19) --------------
      // Three surfaces that used to publish UI_INERT_TAP_DETECTED now ASK for
      // something instead. All three are UI-intent events: they say where the
      // customer should be looking, never what should be selected.
      //
      // 🚨 NONE OF THESE MAY BE ROUTED THROUGH UI_CATEGORY_OPEN_REQUEST /
      // triggerAutoNavigation. That path permanently sets
      // `userHasManuallyInteracted` in md-config-bl-auto-navigation-coordinator.js
      // for every source except AUTO_NAVIGATOR — and AUTO_NAVIGATOR is itself
      // scroll-suppressed in apo_style by design. Either half of that fork is
      // wrong here: one kills auto-navigation for the rest of the session, the
      // other scrolls nowhere. Hence a dedicated, narrow pair of events.
      //
      // Move the page (and restart the ring pulse) for ONE card.
      // Payload: { categoryId, gateId?, source, pulseOnly?, timestamp }.
      // `pulseOnly: true` gives the pulse WITHOUT the scroll — used when the
      // customer's own tap already put the card under their finger.
      // `gateId` addresses an unanswered gate card, which carries
      // data-category-gate rather than data-category and therefore has no
      // categoryId of its own.
      UI_CATEGORY_FOCUS_REQUESTED: 'ui:category_focus_requested',
      // "I'm happy with this answer — what's next?" Consumed by
      // md-config-bl-auto-navigation-coordinator.js, which resolves the next
      // incomplete category and re-publishes UI_CATEGORY_FOCUS_REQUESTED, or
      // UI_BUY_AREA_FOCUS_REQUESTED when the door is finished.
      // Payload: { categoryId, variantId?, source, timestamp }.
      UI_CONFIRM_AND_CONTINUE_REQUESTED: 'ui:confirm_and_continue_requested',
      // Scroll the Add to Cart wrapper into view and pulse its button.
      // Payload: { source, timestamp }.
      UI_BUY_AREA_FOCUS_REQUESTED: 'ui:buy_area_focus_requested',
      // The "Your door is ready!" sticky badge changed state or was used.
      // Published by md-config-ui-ready-badge.js, consumed ONLY by the
      // analytics reporter (config_ready_prompt). `action` is drawn from
      // CONSTANTS.READY_PROMPT_ACTIONS and `context` from
      // CONSTANTS.READY_PROMPT_CONTEXTS -- never free text. `cycle` counts
      // how many times the badge has been shown this page session and is a
      // dedupe discriminator only; it never rides the wire.
      // Payload: { action, context?, cycle, timestamp }.
      UI_READY_PROMPT_INTERACTION: 'ui:ready_prompt_interaction',
      UI_CART_REVEAL_REQUESTED: 'ui:cart_reveal_requested',
      // Cart composite thumbnail. cart-manager publishes REQUESTED with the
      // resolved `_Configuration_ID` right before POST /cart/add.js; the image
      // export manager flattens the APPLIED front-view layers into one PNG and
      // hands it to assets/configuration-image-store.js under that id, so the
      // cart page / drawer can show the door the customer actually built.
      // Payload: { configId, source, timestamp }.
      UI_CART_IMAGE_SNAPSHOT_REQUESTED: 'ui:cart_image_snapshot_requested',
      // Payload: { configId, bytes, layerCount, duration, timestamp }.
      UI_CART_IMAGE_SNAPSHOT_COMPLETED: 'ui:cart_image_snapshot_completed',
      // Payload: { configId, error, timestamp }.
      UI_CART_IMAGE_SNAPSHOT_FAILED: 'ui:cart_image_snapshot_failed',
      // Stored configured-door image prefetch (Gap Plan #25b). The image export
      // manager publishes this BEFORE the click (Add to Cart pointer/focus, or
      // 5 s of configuration idle with the button on screen); cart-manager
      // snapshots + uploads the door once per configuration state so the add
      // can carry _Configuration_Image. A prefetch snapshot reuses
      // UI_CART_IMAGE_SNAPSHOT_REQUESTED with `purpose: 'prefetch'`, which the
      // cart thumbnail store and the reveal gate both ignore.
      // Payload: { source, trigger, timestamp }.
      UI_CONFIGURED_DOOR_IMAGE_PREFETCH_REQUESTED:
        'ui:configured_door_image_prefetch_requested',
      UI_SETUP_BUTTON_HANDLERS: 'ui:setup_button_handlers',
      UI_SHOW_ERROR_NOTIFICATION: 'ui:show_error_notification',
      UI_SHOW_LOADING_SPINNER: 'ui:show_loading_spinner',
      UI_SHOW_MODAL: 'ui:show_modal',
      UI_SIMULATE_CLICK: 'ui:simulate_click',
      UI_SLIDER_BROWSING_UPDATED: 'ui:slider_browsing_updated',
      UI_SLIDER_COMMIT_REQUESTED: 'ui:slider_commit_requested',
      // Brackets the bootstrap-time "accept the default on the customer's
      // behalf" window (seedSliderDefault in md-config-ui-horizontal-slider.js)
      // so a module that must not treat this as customer interaction — e.g.
      // the blueprint overlay's measurement-mode auto-toggle — can ignore
      // SELECTION_BROWSING/SELECTION_APPLIED while it is open. STARTED fires
      // synchronously before the seeded updateSliderPosition() call; COMPLETED
      // fires once the (possibly click-apply-debounced) commit has actually
      // been applied. Deliberately narrow/local — do NOT reuse
      // BUSINESS_CASCADE_AUTO_SELECTION_STARTED/COMPLETED for this, which many
      // unrelated modules (cart-manager, gate-visibility-manager,
      // auto-navigation-coordinator, etc.) already react to for a real bulk
      // cascade.
      UI_SLIDER_DEFAULT_SEED_STARTED: 'ui:slider_default_seed_started',
      UI_SLIDER_DEFAULT_SEED_COMPLETED: 'ui:slider_default_seed_completed',
      UI_SLIDER_DRAG_END: 'ui:slider_drag_end',
      UI_SLIDER_DRAG_MOVE: 'ui:slider_drag_move',
      UI_SLIDER_DRAG_START: 'ui:slider_drag_start',
      UI_SLIDER_FORBIDDEN_STATE_CHANGED: 'ui:slider_forbidden_state_changed',
      UI_SLIDER_INITIALIZED: 'ui:slider_initialized',
      UI_SLIDER_CLEANUP: 'ui:slider_cleanup',
      UI_SLIDER_READY: 'ui:slider_ready',
      UI_SLIDER_RENDERED: 'ui:slider_rendered',
      // Fired when the mobile sticky Add-to-Cart bar is shown/hidden by the
      // rail-ATC IntersectionObserver in md-config-ui-updater.js. Pure
      // instrumentation -- the observer drives the class itself; nothing
      // subscribes to this to DO the hiding. See M0 in
      // .claude/plans/mobile-first-pdp-conversion-plan.md.
      UI_STICKY_BAR_VISIBILITY_CHANGED: 'ui:sticky_bar_visibility_changed',
      UI_SUMMARY_COMPLETION_CHECK_REQUESTED:
        'ui:summary_completion_check_requested',
      UI_SUMMARY_TAB_ACTIVATED: 'ui:summary_tab_activated',
      UI_SWIPE_NAVIGATION_COMPLETED: 'ui:swipe_navigation_completed',
      UI_SWIPE_NAVIGATION_FAILED: 'ui:swipe_navigation_failed',
      UI_SWIPE_NAVIGATION_STARTED: 'ui:swipe_navigation_started',
      UI_SWIPE_NAVIGATION_VALIDATION: 'ui:swipe_navigation_validation',
      UI_TAB_ACTIVATED: 'ui:tab_activated',
      UI_TAB_ACTIVATION_FAILED: 'ui:tab_activation_failed',
      UI_TAB_ACTIVATION_REQUESTED: 'ui:tab_activation_requested',
      UI_TAB_CHANGE_COMPLETED: 'ui:tab_change_completed',
      UI_TAB_CLICKED: 'ui:tab_clicked',
      UI_TAB_DEACTIVATED: 'ui:tab_deactivated',
      UI_TAB_DEACTIVATION_FAILED: 'ui:tab_deactivation_failed',
      UI_TAB_INITIALIZATION_REQUIRED: 'ui:tab_initialization_required',
      UI_TAB_OPEN_REQUEST: 'ui:tab_open_request',
      UI_TAB_OPENED: 'ui:tab_opened',
      UI_TAB_READINESS_CHANGED: 'ui:tab_readiness_changed',
      UI_TAB_TRANSITION_COMPLETED: 'ui:tab_transition_completed',
      UI_TAB_TRANSITION_FAILED: 'ui:tab_transition_failed',
      UI_TAB_TRANSITION_STARTED: 'ui:tab_transition_started',
      UI_TEMPORARY_CLASS: 'ui:temporary_class',
      // Finish-first (Concept A) — inverted Wood & Finish flow in simplified mode
      UI_FINISH_FIRST_RENDERED: 'ui:finish_first_rendered',
      UI_FINISH_FIRST_FINISH_SELECTED: 'ui:finish_first_finish_selected',
      UI_FINISH_FIRST_WOODS_RENDERED: 'ui:finish_first_woods_rendered',
      UI_FINISH_FIRST_FAILED: 'ui:finish_first_failed',
      // Numeric jamb-depth input selector — replaces a large variant-tile wall
      // with a typed-measurement field on products that opt in (see
      // NUMERIC_INPUT_PRODUCTS below).
      UI_NUMERIC_INPUT_APPLIED: 'ui:numeric_input_applied',
      UI_NUMERIC_INPUT_FAILED: 'ui:numeric_input_failed',
      UI_THUMBNAIL_CLICKED: 'ui:thumbnail_clicked',
      UI_THUMBNAIL_COMPOSITE_DETECTED: 'ui:thumbnail_composite_detected',
      UI_THUMBNAIL_DIRECT_VARIANT_DETECTED:
        'ui:thumbnail_direct_variant_detected',
      UI_THUMBNAIL_GENERATED: 'ui:thumbnail_generated',
      UI_THUMBNAIL_MULTI_VARIANT_DETECTED:
        'ui:thumbnail_multi_variant_detected',
      UI_THUMBNAIL_SELECTION_COMPLETED: 'ui:thumbnail_selection_completed',
      UI_THUMBNAIL_SELECTION_FAILED: 'ui:thumbnail_selection_failed',
      UI_THUMBNAIL_SINGLE_VARIANT_DETECTED:
        'ui:thumbnail_single_variant_detected',
      UI_THUMBNAIL_VARIANT_RESOLVED: 'ui:thumbnail_variant_resolved',
      UI_THUMBNAILS_GENERATION_COMPLETED: 'ui:thumbnails_generation_completed',
      UI_THUMBNAILS_GENERATION_FAILED: 'ui:thumbnails_generation_failed',
      UI_THUMBNAILS_GENERATION_STARTED: 'ui:thumbnails_generation_started',
      UI_TOOLTIP_CACHE_FAILED: 'ui:tooltip_cache_failed',
      UI_TOOLTIP_CACHE_STARTED: 'ui:tooltip_cache_started',
      UI_TOOLTIP_CACHE_SUCCESS: 'ui:tooltip_cache_success',
      UI_TOOLTIP_DISPLAY_FAILED: 'ui:tooltip_display_failed',
      UI_TOOLTIP_DISPLAY_STARTED: 'ui:tooltip_display_started',
      UI_TOOLTIP_DISPLAY_SUCCESS: 'ui:tooltip_display_success',
      UI_TOOLTIP_ELEMENTS_DISCOVERED: 'ui:tooltip_elements_discovered',
      UI_TOOLTIP_MANAGER_CLEANUP: 'ui:tooltip_manager_cleanup',
      UI_TOOLTIP_MANAGER_INIT_REQUESTED: 'ui:tooltip_manager_init_requested',
      UI_TOOLTIP_MANAGER_READY: 'ui:tooltip_manager_ready',
      UI_TOOLTIP_STATE_CHANGED: 'ui:tooltip_state_changed',
      UI_TOOLTIP_TRIGGERED: 'ui:tooltip_triggered',
      UI_TOOLTIPS_INITIALIZATION_FAILED: 'ui:tooltips_initialization_failed',
      UI_TOOLTIPS_INITIALIZATION_REQUESTED:
        'ui:tooltips_initialization_requested',
      UI_UPDATE_CART_UI: 'ui:update_cart_ui',
      UI_UPDATE_ELEMENTS: 'ui:update_elements',
      UI_UPDATE_FORBIDDEN_STATE: 'ui:update_forbidden_state',
      UI_UPDATE_PREEMPTIVE_FORBIDDEN: 'ui:update_preemptive_forbidden',
      UI_UPDATE_PREEMPTIVE_FORBIDDEN_COMPLETE:
        'ui:update_preemptive_forbidden_complete',
      UI_UPDATE_PREEMPTIVE_FORBIDDEN_FAILED:
        'ui:update_preemptive_forbidden_failed',
      UI_UPDATE_REQUESTED: 'ui:update_requested',
      UI_UPDATER_CLEANUP_COMPLETE: 'ui:updater_cleanup_complete',
      UI_MATERIAL_FILTER_RERENDER_START: 'ui:material_filter_rerender_start',
      UI_MATERIAL_FILTER_RERENDER_COMPLETE:
        'ui:material_filter_rerender_complete',
      UI_MATERIAL_FILTER_RERENDER_FAILED: 'ui:material_filter_rerender_failed',
      // apo_style material remap (md-config-bl-material-remap-coordinator.js).
      // CONFLICT is published when a late wood/finish change leaves one or
      // more applied selections with no equivalent in the new material; the
      // two _REMAP_ answers are the customer's response to that warning.
      UI_MATERIAL_REMAP_CONFLICT: 'ui:material_remap_conflict',
      UI_MATERIAL_REMAP_KEEP_EXISTING: 'ui:material_remap_keep_existing',
      UI_MATERIAL_REMAP_START_FRESH: 'ui:material_remap_start_fresh',
      UI_SELECTION_APPLIED_DISPLAY_UPDATE_FAILED:
        'ui:selection_applied_display_update_failed',
      UI_FORBIDDEN_BROWSING_FEEDBACK_FAILED:
        'ui:forbidden_browsing_feedback_failed',
      UI_FORBIDDEN_APPLIED_CONFLICT_FEEDBACK_FAILED:
        'ui:forbidden_applied_conflict_feedback_failed',
      UI_STALE_SELECTION_CLEARED: 'ui:stale_selection_cleared',
      UI_STALE_SELECTION_DETECTED: 'ui:stale_selection_detected',
      UI_USER_UNAPPLY_REQUESTED: 'ui:user_unapply_requested',
      // Short customer-facing notice when an ENFORCED_PAIR rule changes a
      // category the customer did not just touch. Payload: { categoryId, message }.
      UI_ENFORCED_PAIR_NOTICE_REQUESTED: 'ui:enforced_pair_notice_requested',
      UI_VARIANT_HTML_REQUESTED: 'ui:variant_html_requested',
      UI_VARIANTS_VIEW_NEEDS_RERENDER: 'ui:variants_view_needs_rerender',
      UI_CONFIGURATOR_SLIDE_FOCUSED: 'ui:configurator_slide_focused',
      UI_VIEW_TOGGLED: 'ui:view_toggled',
      UI_WRAPPER_CREATED: 'ui:wrapper_created',
      UI_FULLSCREEN_ENTERED: 'ui:fullscreen_entered',
      UI_FULLSCREEN_EXITED: 'ui:fullscreen_exited',
      UI_FULLSCREEN_EXIT_REQUESTED: 'ui:fullscreen_exit_requested',

      // =========================================================================
      // VALIDATION EVENTS (validation:*) - 3 events
      // Configuration validation and error reporting
      // =========================================================================
      VALIDATION_CONFIGURATION_COMPLETE: 'validation:configuration_complete',
      VALIDATION_ERROR: 'validation:error',
      VALIDATION_TAB_CHANGE_FAILED: 'validation:tab_change_failed',
    }),

    // =============================================================================
    // EVENT NAMESPACES - For prefix matching (23 namespaces)
    // =============================================================================
    EVENT_NAMESPACES: Object.freeze({
      ANALYTICS: 'analytics:',
      BUSINESS: 'business:',
      CONDUCTOR: 'conductor:',
      DATA: 'data:',
      DEBUG: 'debug:',
      ERROR: 'error:',
      FORBIDDEN: 'forbidden:',
      KLAVIYO: 'klaviyo:',
      MATERIAL: 'material:',
      MODULE: 'module:',
      PERFORMANCE: 'performance:',
      PERSISTENCE: 'persistence:',
      PPD: 'ppd:',
      PRICE: 'price:',
      RECOVERY: 'recovery:',
      RESTORATION: 'restoration:',
      SELECTION: 'selection:',
      STATE: 'state:',
      SUPPORT: 'support:',
      SYSTEM: 'system:',
      TEST: 'test:',
      UI: 'ui:',
      VALIDATION: 'validation:',
    }),
  });

  // ✅ Core constants module loaded successfully
  // (Note: Can't use utils.isDevelopment() here - utils.js loads AFTER constants.js)
})();
